import i18nFactory from '@yandex-int/i18n';
import { FetchResult } from 'apollo-boost';
import { ApolloQueryResult } from 'apollo-client';
import {
    compact,
    get,
    groupBy,
    isArray,
    isUndefined,
    map,
    mapValues,
    mergeWith,
    omit,
    pick,
    reduce,
    sortBy,
    uniq,
    uniqueId
} from 'lodash';
import moment from 'moment';

import { SaveGroupsInput } from '../../api/groups/GroupsMutationExecutor';
import { SelectorItems } from '../../components/CampaignAndGroupSelector/Selector/Selector';
import { GROUP_PRIORITY } from '../../constants';
import {
    getGroupFilterCriteriaContainers,
    GroupCriteriaKey,
    StatisticsByAllGoals
} from '../../declarations/filter/filter';
import { defaultRegionsEditorData } from '../../reducers/regionsEditor';
import { cryptaAudioGenresEditorsInitialState } from '../../reducers/targetings/cryptaAudioGenres';
import { selectEditPageItemNameEditorValue } from '../../selectors/editPageItemNameEditor/editPageItemNameEditor';
import { getInternalType } from '../../service/CampaignHelpers/CampaignHelpers';
import { flattenContainer, getStrictExpressions } from '../../service/FilterHelpers/FilterHelpers';
import { GridScreenLinkBuilder } from '../../service/GridScreenLinkBuilder';
import { GroupsHelpers } from '../../service/GroupsHelpers/GroupsHelpers';
import { fetchInterfaceLangs } from '../../service/InternalGroupHelpers/InternalGroupHelpers';
import { getGroupKeywordsEditorId } from '../../service/keywordsEditor';
import { mapPhraseWithMinusWordsToString } from '../../service/PhrasesHelpers/PhrasesHelpers';
import { mapPiTagsFromApi, mapPiTagsToApi } from '../../service/PiTagsHelpers';
import { RegionsHelpers } from '../../service/RegionsHelpers/RegionsHelpers';
import { getTagCloudTargetingId } from '../../service/TagCloudTargetingHelper/TagCloudTargetingHelper';
import { VirtualTreeHelpers } from '../../service/VirtualTreeHelpers/VirtualTreeHelpers';
import { AudienceTargetingsType, AudienceTargetingsValue } from '../../typings/audienceTargetings';
import {
    banners,
    ClientInfo,
    groups,
    NumberRangeInputValues,
    Region,
    RegionId,
    ValidationResultItem
} from '../../typings/common';
import { CryptaSegmentsByType } from '../../typings/cryptaSegments';
import { DatesIntervalEditorsState } from '../../typings/datesInterval';
import {
    BannersGroupBaseInfo,
    BannersGroupBaseInfoExtension,
    CampaignsCalcTypeFilter,
    CampaignType,
    ClientData,
    FeedFiltersGroupBaseInfo,
    GdGroupStatus,
    Group,
    GroupBaseInfo,
    GroupType
} from '../../typings/domain';
import { EditPageItemNameEditorsState } from '../../typings/editPageItemNameEditor';
import {
    Criteria,
    FilterExpression,
    FilterPartialExpression,
    GoalStatsQueryFilter,
    GroupsQueryFilter,
    StatsQueryFilter
} from '../../typings/filter';
import {
    AddSmartFilters,
    adGroupsRowFragmentForEdit,
    adGroupsRowFragmentForEdit_GdContentPromotionAdGroup_libraryMinusKeywordsPacks,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers,
    adGroupsRowFragmentForEdit_GdCpmPriceAdGroup,
    adGroupsRowFragmentForEdit_GdInternalAdGroup,
    adGroupsRowFragmentForEdit_GdSmartAdGroup,
    AdGroupTruncatedFragment,
    adPreviewFragment,
    adsGroupRowFragment,
    CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    CampaignsSuggestListQuery_client_campaigns_rowset,
    CopyAdGroupsInfoQuery,
    GdAddContentPromotionAdGroupItemInput,
    GdAddInternalAdGroupsItemInput,
    GdAddSmartFilterConditionInput,
    GdAddSmartFiltersInput,
    GdAdGroupPrimaryStatusInput,
    GdAdGroupType,
    GdBidModifierType,
    GdCampaignType,
    GdContentPromotionGroupTypeInput,
    GdCpmGroupTypeInput,
    GdiRecommendationType,
    GdShowConditionAutobudgetPriority,
    GdShowConditionAutobudgetPriorityInput,
    GdShowConditionType,
    GdSmartFilterConditionOperator,
    GdSmartFilterConditionOperatorInput,
    GdSmartFilterTab,
    GdSmartFilterTabInput,
    GdSmartFilterTargetFunnel,
    GdSmartFilterTargetFunnelInput,
    GdStrategyType,
    GdStringDictName,
    GdUpdateAdGroupKeywordItemInput,
    GdUpdateAdGroupRelevanceMatchItemInput,
    GdUpdateContentPromotionAdGroupItemInput,
    GdUpdateCpmAdGroupInput,
    GdUpdateCpmAdGroupItemInput,
    GdUpdateInternalAdGroupsItemInput,
    GdUpdateMcBannerAdGroupItemInput,
    GdUpdatePerformanceAdGroupItemInput,
    GdUpdateSmartFiltersInput,
    GdUpdateTextAdGroupItemInput,
    GroupsQueryForEdit,
    GroupsQueryForEdit_client_adGroups_rowset,
    GroupsQueryForEdit_client_feeds_rowset,
    GroupsQueryForEdit_client_feeds_rowset_categories,
    GroupsQueryForEdit_client_retargetings_rowset,
    GroupsQueryForEdit_client_showConditions_rowset,
    GroupsQueryForEdit_client_smartFilters_rowset,
    GroupsQueryForEdit_client_smartFilters_rowset_conditions,
    InterfaceLangInput,
    RecommendationsAdsGroupEditQuery_client_adGroups_rowset,
    RecommendationsGroupEditQuery,
    RegionTreeForGroupEdit,
    RegionTreeForGroupEdit_client_campaigns_rowset_GdPriceCampaign,
    RegionTreeForGroupEdit_client_campaigns_rowset_GdPriceCampaign_customGeoExpanded_geoExpandedTrees,
    RetargetingConditionsQueryForGroupEdit,
    RetargetingConditionsQueryForUserProfile,
    RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset,
    SmartFeedSchemaQuery,
    SmartFeedSchemaQuery_constants_smartFilterSchemas_fields,
    SmartFeedSchemaQuery_constants_smartFilterSchemas_fields_GdSmartFilterStringField_operators,
    UaTraitsQuery,
    UaTraitsQuery_constants_stringDicts_entries_GdStringDictEntryWithLongKey,
    UpdateSmartFilters,
    ValidationError
} from '../../typings/graphql-grid';
import { GridSourceParams } from '../../typings/grid';
import {
    DuplicateItem,
    GroupId,
    GroupsCategories,
    GroupsStateMap,
    GroupsTypeMap,
    SmartGroupBudgetCalculation,
    StringDictsItems
} from '../../typings/groups';
import {
    ConditionsKeys,
    CpmGroupShowConditionType,
    DataForEditGroups,
    EditBaseGroupDataValue,
    EditContentPromotionGroupDataValue,
    EditCpmGroupDataValue,
    EditCpmPriceGroupDataValue,
    EditGroupAutotargeting,
    EditGroupData,
    EditGroupDataValue,
    EditGroupFilterItem,
    EditGroupRegions,
    EditInternalGroupData,
    EditInternalGroupDataValue,
    EditMcBannerGroupDataValue,
    EditPerformanceGroupDataValue,
    EditTextGroupDataValue,
    Feed,
    FeedCategory,
    FeedFilterCampaign,
    FeedFilterCondition,
    FeedFilterEnumFieldParams,
    FeedFilterOperationControlValues,
    FeedFilterSchema,
    FeedFilterSchemaField,
    FeedFilterSchemaFieldType,
    FeedFilterSchemaOperator,
    Feeds,
    MassActionId,
    ProductsList,
    ProductsListItem,
    ProductsListItemRestriction,
    ProductsListItemRestrictionCondition,
    ProductsRestrictionNestedConditions,
    RetargetingValue,
    UpdateRetargetingConditionsForGroupsEdit
} from '../../typings/groupsEditState';
import {
    AdditionalTargetingField,
    ApiFormatTargetingShowDates,
    TargetingShowDates
} from '../../typings/internalGroups';
import { KeywordsData, KeywordsEditorData } from '../../typings/keywordsEditorData';
import { MinusPhrasesShortLibraryItem } from '../../typings/minusPhraseLibrary';
import { PITagsEditorState } from '../../typings/piTags';
import { PricePackagesForGroup } from '../../typings/pricePackages';
import {
    RateCorrection,
    RateCorrectionEditorState,
    RateCorrectionType,
    RatesCorrectionsConstantsData,
    RatesCorrectionsValue
} from '../../typings/ratesCorrections';
import {
    EditPageRecommendationsData,
    RecommendationCharacter,
    RecommendationsIconType
} from '../../typings/recommendations';
import { RegionsEditorState } from '../../typings/regionsEditor';
import {
    RetargetingAudienceEditorState,
    RetargetingConditionAudienceEditorState,
    RetargetingConditionData,
    RetargetingEditorState
} from '../../typings/retargeting';
import {
    AudienceTargetingsEditorsState,
    ClientFeatures,
    CryptaAudioGenresEditorsState,
    CryptaBehavioralEditorsState,
    CryptaGroupFamilyEditorsState,
    CryptaSocialDemoEditorsState,
    InterestsEditorsState,
    MinusPhraseLibraryEditorState,
    PITagsEditorsState,
    StringDicts,
    TargetTagsEditorsState
} from '../../typings/state';
import { TagCloudTargetingsState } from '../../typings/tagCloudTargeting';
import { TargetPlatform, TargetPlatformTags } from '../../typings/targetTagsEditor';
import { UserProfileRetargetingCondition } from '../../typings/userProfileRetargeting';
import { mapRowGroupToBannerPreviewData } from '../banner/banner';
import { makeAudienceCollectionForApi, makeAudienceCollectionFromApi } from '../banners-edit/api-data-mappers';
import { mapRowToCampaignTruncatedBaseInfo } from '../campaign';
import {
    getHrefForDomainFromApiFormat,
    getValueFromApiFormat,
    mapAggregatedStatusInfoCounterChild,
    mapCampaignRowTypeToCampaignType,
    mapGroupRowTypeToGroupType,
    mapJoinTypeToApiFormat,
    mapStatsDataToStats,
    mapTargetingModeToApiFormat
} from '../common/common';
import { FilterCriteriaMappings, textToGroupFilterExpression } from '../filter/filter';
import { mapRowGoals } from '../goal/goal';
import {
    mapTagCloudRadioTargetingEditorData,
    mapTagCloudTargetingSuggestStringDictValue,
    mapTagCloudTargetingSuggestValue,
    mapTagCloudTargetingSuggestWithVersionValue,
    mapTagCloudTargetingTextValue,
    mapTagCloudTargetingToDeviceOptionsRecord
} from '../internalGroup';
import { mapDisplayPageBlockFromApiFormat } from '../placementsMap/placementsMap';
import { mapPricePackagesDataForGroup } from '../pricePackages/pricePackages';
import {
    getAllowedRateCorrectionsDataForGroup,
    mapEditorRateCorrectionsToRateCorrections,
    mapRateCorrectionsCpmBannerData,
    mapRateCorrectionsData,
    mapRatesCorrectionsConstantsFromApiFormat,
    mapRatesCorrectionsValueToApiFormatForGroup
} from '../rateCorrections/rateCorrections';
import {
    isRecommendationSupported,
    isRecommendationSupportedOnEditGroupPage,
    isRecommendationSupportedOnGridByGroupType,
    mapBaseRecommendationInfoFromApiFormat,
    mapContextRecommendationFromApiFormat
} from '../recommendation';
import {
    mapRetargetingConditionFromApiFormat,
    mapRetargetingEditorValueToGroupRetargetingValue
} from '../retargeting/retargeting';
import {
    mapAudienceTargetingsToRetargetingConditionsApiFormat,
    mapUserProfileRetargetingConditionFromApiFormat
} from '../retargetingCondition/retargetingCondition';

import * as keyset from './group.i18n';

const i18n = i18nFactory(keyset);

const mapGroupStatusFromApiFormat = (status: adsGroupRowFragment['status']): GdGroupStatus => {
    const { primaryStatus, primaryStatusDesc, moderationStatus, bsEverSynced, blGenerationStatus } = status;
    return {
        primaryStatus,
        primaryStatusDesc: getValueFromApiFormat(primaryStatusDesc),
        moderationStatus,
        bsEverSynced,
        blGenerationStatus: getValueFromApiFormat(blGenerationStatus)
    };
};

export const mapVersionToApiFormat = (version?: string | null): string | null => {
    if (!version) {
        return null;
    }

    const splitVersion = version.split('.');

    if (splitVersion.length === 1) {
        return `${version}.0`;
    }

    return version;
};

export const mapVersionFromApiFormat = (version: string | null): string | null => {
    if (!version) {
        return null;
    }

    const splitVersion = version.split('.');

    if (splitVersion.length === 2 && splitVersion[1] === '0') {
        return splitVersion[0];
    }

    return version;
};

const mapRowToGroupBaseInfo = (
    params: GridSourceParams,
    client: ClientData
): ((row: adsGroupRowFragment) => GroupBaseInfo) => (row: adsGroupRowFragment): GroupBaseInfo => {
    return {
        id: String(row.id),
        type: mapGroupRowTypeToGroupType(row.type),
        campaign: mapRowToCampaignTruncatedBaseInfo(row.campaign, client, params),
        name: row.name,
        adsCount: row.adsCount,
        groupUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            groups,
            banners,
            {
                login: params.login,
                campaignsIds: [String(row.campaign.id)],
                contextIds: [String(row.id)]
            },
            params
        ),
        access: row.access,
        status: mapGroupStatusFromApiFormat(row.status),
        regionIds: row.regionsInfo.regionIds,
        minusKeywords: row.minusKeywords
    };
};

export const mapRowsToGroupsForCopy = (
    items: CampaignsSuggestListQuery_client_campaigns_rowset[],
    campaignType?: CampaignType
): SelectorItems[] =>
    items.reduce((acc: SelectorItems[], item: CampaignsSuggestListQuery_client_campaigns_rowset) => {
        const campaignTypeCurrent = mapCampaignRowTypeToCampaignType(item.type);

        if (campaignTypeCurrent === campaignType || campaignType === undefined) {
            const campaign: SelectorItems = {
                id: String(item.id),
                name: item.name,
                type: campaignTypeCurrent
            };

            acc.push(campaign);
        }

        return acc;
    }, []);

const mapRowsToBannersGroupBaseInfo = (
    params: GridSourceParams,
    client: ClientData
): ((row: adsGroupRowFragment) => BannersGroupBaseInfo) => (row: adsGroupRowFragment): BannersGroupBaseInfo => {
    const groupBaseInfo = mapRowToGroupBaseInfo(params, client)(row);
    const groupHref = getGroupHrefFromApiFormat(row);

    return {
        ...groupBaseInfo,
        ...groupHref
    };
};

export function mapRowsToBannersGroupsBaseInfo(
    groupRows: adsGroupRowFragment[],
    client: ClientData,
    params: GridSourceParams
): BannersGroupBaseInfo[] {
    return groupRows.map(mapRowsToBannersGroupBaseInfo(params, client));
}

export function mapRowsToGroupsTruncatedBaseInfo(
    row: AdGroupTruncatedFragment,
    client: ClientData,
    params: GridSourceParams
): GroupBaseInfo {
    const campaign = mapRowToCampaignTruncatedBaseInfo(row.campaign, client, params);

    return {
        id: String(row.id),
        campaign: campaign,
        name: row.name,
        access: row.access,
        adsCount: row.adsCount,
        groupUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            groups,
            banners,
            {
                login: params.login,
                campaignsIds: [String(row.campaign.id)],
                contextIds: [String(row.id)]
            },
            params
        ),
        status: mapGroupStatusFromApiFormat(row.status),
        regionIds: row.regionsInfo.regionIds,
        minusKeywords: row.minusKeywords,
        type: mapGroupRowTypeToGroupType(row.type)
    };
}

export function mapRowsToFeedFilterGroupTruncatedInfo(
    row: AdGroupTruncatedFragment,
    client: ClientData,
    params: GridSourceParams
): FeedFiltersGroupBaseInfo {
    const groupBaseInfo = mapRowsToGroupsTruncatedBaseInfo(row, client, params);

    if (row.__typename === 'GdSmartAdGroup') {
        return {
            ...groupBaseInfo,
            feedId: row.feedId
        };
    }

    throw new Error('Cannot map not smart feed filter group');
}

export function mapRowsToGroups(
    groupRows: adsGroupRowFragment[],
    client: ClientData,
    params: GridSourceParams
): Group[] {
    return groupRows.map(row => {
        const groupBaseInfo = mapRowToGroupBaseInfo(params, client)(row);
        const rowGoalStats = row.goalStats || [];
        const recommendations = (row.recommendations || [])
            .filter(
                recommendation =>
                    // isRecommendationSupportedOnGridByGroupType - рекомендации которые поддерживаются только для определенных групп
                    isRecommendationSupported(recommendation.type) ||
                    isRecommendationSupportedOnGridByGroupType(recommendation.type, row.type)
            )
            .map(mapContextRecommendationFromApiFormat);
        const { aggregatedStatusInfo } = row;

        return {
            ...groupBaseInfo,
            access: row.access,
            type: mapGroupRowTypeToGroupType(row.type),
            campaignType: groupBaseInfo.campaign.type,
            stats: mapStatsDataToStats(row.stats),
            goalStats: mapRowGoals(rowGoalStats),
            recommendations: {
                data: recommendations.length !== 0 ? recommendations : undefined,
                recommendationsIconType: recommendations.some(
                    recommendation => recommendation.character === RecommendationCharacter.attention
                )
                    ? RecommendationsIconType.red
                    : RecommendationsIconType.yellow
            },
            minusKeywords: row.minusKeywords,
            regionIds: row.regionsInfo.regionIds,
            availableShowConditionTypes: row.availableShowConditionTypes,
            aggregatedStatus: row.aggregatedStatus,
            aggregatedStatusInfo:
                aggregatedStatusInfo !== null
                    ? {
                          status: aggregatedStatusInfo.status,
                          reasons: compact(aggregatedStatusInfo.reasons),
                          counters: {
                              ads: mapAggregatedStatusInfoCounterChild(aggregatedStatusInfo.counters.ads),
                              keywords: mapAggregatedStatusInfoCounterChild(aggregatedStatusInfo.counters.keywords)
                          }
                      }
                    : undefined
        };
    });
}

const mappings = new FilterCriteriaMappings<GroupsQueryFilter & StatsQueryFilter & GoalStatsQueryFilter>();
const { toNumber, toEnum } = mappings.valueMappings;

const groupCriteriaMappings: { [key in GroupCriteriaKey]: (expression: FilterExpression) => GroupsQueryFilter } = {
    id: mappings.includes('adGroupIdIn', 'adGroupIdNotIn'),
    name: mappings.contains('nameContains', 'nameNotContains'),
    nameMinusPhrasePacks: mappings.contains('mwPackNameContains', 'mwPackNameNotContains'),
    status: mappings.equality('primaryStatusIn', toEnum(GdAdGroupPrimaryStatusInput)),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison('minProfitability', 'maxProfitability', toNumber),
    avgGoalCost: (expression: FilterExpression): GroupsQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minCostPerAction',
                'maxCostPerAction',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minAvgGoalCost', 'maxAvgGoalCost', toNumber)(expression);
    },
    avgClickCost: mappings.comparison('minAvgClickCost', 'maxAvgClickCost', toNumber),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    recommendations: mappings.equality('recommendations', toEnum(GdiRecommendationType)),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: (expression: FilterExpression): GroupsQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison('minGoals', 'maxGoals', toNumber, false, expression.additionalCriteriaKey)(
                expression
            );
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(expression);
    },
    conversionRate: (expression: FilterExpression): GroupsQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minConversionRate',
                'maxConversionRate',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minConversionRate', 'maxConversionRate', toNumber)(expression);
    },
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber),
    libraryMinusPhrasePacks: mappings.includes('libraryMwIdIn', 'libraryMwIdNotIn')
};

const expressionToGroupsQueryFilter = (expression: FilterExpression): GroupsQueryFilter => {
    return groupCriteriaMappings[expression.criteriaKey](expression);
};

export const mapGroupFilterToApiFormat = (
    expressions: FilterPartialExpression[],
    clientInfo: ClientInfo,
    calcType?: CampaignsCalcTypeFilter
): GroupsQueryFilter => {
    const criteria: Criteria[] = [];

    getGroupFilterCriteriaContainers(clientInfo, calcType === CampaignsCalcTypeFilter.CPM).forEach(({ criteria: c }) =>
        criteria.push(...flattenContainer(c))
    );

    return getStrictExpressions(expressions, criteria, textToGroupFilterExpression)
        .map(expressionToGroupsQueryFilter)
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export const isGroupEditable = (group: GroupBaseInfo): boolean => {
    return group.access.canEdit;
};

export const getGroupHrefFromApiFormat = (group: adsGroupRowFragment): BannersGroupBaseInfoExtension => {
    switch (group.__typename) {
        case 'GdDynamicAdGroup':
            return {
                mainDomain: getHrefForDomainFromApiFormat(group.mainDomain)
            };
        case 'GdMobileContentAdGroup':
            return {
                storeHref: getValueFromApiFormat(group.storeHref)
            };
        default:
            return {};
    }
};

export const getNewGroupName = (): string => {
    return i18n('newGroup');
};

const getNewAudienceName = (): string => {
    return i18n('newAudience');
};

type GroupShowConditionsData = {
    keywords: KeywordsData[];
    autotargeting: EditGroupAutotargeting;
};

const getShowConditionsEmptyData = (): GroupShowConditionsData => {
    return {
        keywords: [],
        autotargeting: {
            isActive: false
        }
    };
};

function mapTextGroupData(
    groupRow: adGroupsRowFragmentForEdit,
    showConditionsData: GroupShowConditionsData,
    groupRetargetingConditions: Array<RetargetingValue>,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType> = [],
    retargetingCondition?: UserProfileRetargetingCondition
): EditTextGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);

    return {
        campaignType: CampaignType.TEXT,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        keywords: showConditionsData.keywords,
        minusKeywords: groupRow.minusKeywords || [],
        selectedLibraryList: mapSelectedLibraryList(groupRow.libraryMinusKeywordsPacks),
        autotargeting: showConditionsData.autotargeting,
        retargeting: groupRetargetingConditions,
        rateCorrections: mapRateCorrectionsData(groupRow.bidModifiers, availableBidModifiersTypes),
        generalRate: undefined,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        audienceTargetings: retargetingCondition ? retargetingCondition.audienceTargetings : {},
        audienceTargetingsTemplateName: retargetingCondition ? retargetingCondition.name : getNewAudienceName(),
        piTagsEditor: {}
    };
}

function mapMcBannerGroupData(
    groupRow: adGroupsRowFragmentForEdit,
    showConditionsData: GroupShowConditionsData,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType> = []
): EditMcBannerGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);

    return {
        campaignType: CampaignType.MCBANNER,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        keywords: showConditionsData.keywords,
        minusKeywords: groupRow.minusKeywords || [],
        selectedLibraryList: mapSelectedLibraryList(groupRow.libraryMinusKeywordsPacks),
        rateCorrections: mapRateCorrectionsData(groupRow.bidModifiers, availableBidModifiersTypes),
        generalRate: undefined,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        piTagsEditor: {}
    };
}

function mapCpmGroupData(
    groupRow: adGroupsRowFragmentForEdit,
    showConditionsData: GroupShowConditionsData,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType> = [],
    generalRate?: number,
    retargetingCondition?: UserProfileRetargetingCondition,
    productsList?: Record<string, ProductsListItem>
): EditCpmGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);
    const { rateCurrentType, rateCorrections } = getCurrentRateTypeAndLists({
        productRestrictionId: groupRow.productRestrictionId,
        rateCorrectionsFromApi: groupRow.bidModifiers,
        productsList: productsList
    });
    const videoGoals =
        (groupRow.__typename === 'GdCpmVideoAdGroup' ||
            groupRow.__typename === 'GdCpmOutdoorAdGroup' ||
            groupRow.__typename === 'GdCpmIndoorAdGroup' ||
            groupRow.__typename === 'GdCpmAudioAdGroup') &&
        'videoGoals' in groupRow
            ? groupRow.videoGoals
            : [];

    const groupType = mapGroupRowTypeToGroupType(groupRow.type);

    return {
        campaignType: CampaignType.CPM_BANNER,
        groupType,
        name: groupRow.name,
        productRestrictionId: String(groupRow.productRestrictionId) || '',
        keywords: showConditionsData.keywords,
        autotargeting: showConditionsData.autotargeting,
        showConditionType: groupRow.availableShowConditionTypes.some(
            conditionType => conditionType === GdShowConditionType.USER_PROFILE
        )
            ? CpmGroupShowConditionType.USER_PROFILE
            : CpmGroupShowConditionType.KEYWORD,
        rateCorrections,
        rateCurrentType,
        generalRate,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        outdoorMap:
            groupType === GroupType.CPM_OUTDOOR && 'pageBlocks' in groupRow
                ? {
                      pageBlocks: mapDisplayPageBlockFromApiFormat(groupRow.pageBlocks)
                  }
                : undefined,
        indoorMap:
            groupType === GroupType.CPM_INDOOR && 'pageBlocks' in groupRow
                ? {
                      pageBlocks: mapDisplayPageBlockFromApiFormat(groupRow.pageBlocks)
                  }
                : undefined,
        audienceCollection: videoGoals.map(makeAudienceCollectionFromApi),
        audienceTargetingsTemplateName: retargetingCondition ? retargetingCondition.name : '',
        audienceTargetings: retargetingCondition ? retargetingCondition.audienceTargetings : {},
        piTagsEditor: {}
    };
}

function mapCpmPriceGroupData(
    groupRow: adGroupsRowFragmentForEdit_GdCpmPriceAdGroup,
    regionTree: Region[],
    retargetingCondition?: UserProfileRetargetingCondition,
    pricePackage?: PricePackagesForGroup
): EditCpmPriceGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);
    const behaviors = get(retargetingCondition, 'audienceTargetings.behaviors');
    const viewTypes = get(groupRow.campaign, 'flightTargetingsSnapshot.viewTypes');
    const defaultBehaviors = get(groupRow.campaign, 'flightTargetingsSnapshot.behaviors');
    const groupType = mapGroupRowTypeToGroupType(groupRow.type);
    const audienceTargetings = retargetingCondition ? retargetingCondition.audienceTargetings : {};

    return {
        campaignType: CampaignType.CPM_PRICE,
        groupType,
        name: groupRow.name,
        productRestrictionId: String(groupRow.productRestrictionId) || '',
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        behaviors: behaviors || [],
        defaultBehaviors: defaultBehaviors || [],
        audienceTargetingsTemplateName: retargetingCondition ? retargetingCondition.name : '',
        audienceTargetings: omit(audienceTargetings, [AudienceTargetingsType.behaviors]),
        viewTypes: viewTypes || [],
        piTagsEditor: {},
        pricePackage: pricePackage,
        isDefault: Number(groupRow.priority) === 0
    };
}

export function getCurrentRateTypeAndLists(parameters: {
    productRestrictionId: string | null;
    rateCorrectionsFromApi: Array<adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_bidModifiers> | null;
    productsList?: Record<string, ProductsListItem>;
}): Pick<EditCpmGroupDataValue, 'rateCurrentType' | 'rateCorrections'> {
    const { productRestrictionId, rateCorrectionsFromApi, productsList } = parameters;
    const rateCorrectionsByType = productsList && productsList[CampaignType.CPM_BANNER];
    const rateCorrectionByRestrictions =
        rateCorrectionsByType && rateCorrectionsByType.restrictions ? rateCorrectionsByType.restrictions : [];
    const rateCorrectionsLists = rateCorrectionByRestrictions.find(
        (restrict: ProductsListItemRestriction) => restrict.id === productRestrictionId + ''
    );
    const settingsForRate = rateCorrectionsLists
        ? rateCorrectionsLists.conditions
              .filter((item: ProductsListItemRestrictionCondition) => item.name === ConditionsKeys.correction)
              .map((item: ProductsListItemRestrictionCondition) => item.nestedConditions)[0]
        : [];
    const rateCorrections = mapRateCorrectionsCpmBannerData(rateCorrectionsFromApi, settingsForRate);
    const keyRate = Object.keys(rateCorrections).shift() || RateCorrectionType.retargeting;
    const rateCurrentType =
        (rateCorrections[keyRate] && rateCorrections[keyRate].type) || RateCorrectionType.retargeting;

    return {
        rateCurrentType,
        rateCorrections
    };
}

export function getGeoproductTargetPlatforms(
    productsList: Record<string, ProductsListItem>
): ProductsRestrictionNestedConditions[] {
    const geoproductRestictions = productsList[GdCampaignType.CPM_BANNER].restrictions.find(
        ({ groupType }) => groupType === GroupType.CPM_GEOPRODUCT
    );

    if (!geoproductRestictions) {
        return [];
    }

    const condition = geoproductRestictions.conditions.find(cond => cond.name === ConditionsKeys.bs_target_tags);

    return (condition && condition.nestedConditions) || [];
}

export function getTargetPlatformsData(params: {
    selectedTags?: string[] | null;
    productsList?: Record<string, ProductsListItem>;
}): Record<string, TargetPlatform> | undefined {
    const { selectedTags, productsList } = params;

    if (!productsList) {
        return;
    }

    const allowedPlatforms = getGeoproductTargetPlatforms(productsList);

    return allowedPlatforms.reduce((acc, platform) => {
        acc[platform] = {
            tag: platform,
            selected: (selectedTags || []).includes(platform)
        };

        return acc;
    }, {});
}

export function getTargetTagsEditorsState(
    groupListValues: EditGroupData[],
    productsList?: Record<string, ProductsListItem>,
    selectedPlatforms?: TargetPlatformTags[]
): TargetTagsEditorsState {
    return groupListValues.reduce((acc, group) => {
        const editorId = GroupsHelpers.getGroupEditPageItemEditorId(group.id);

        if (group.value.campaignType === CampaignType.CPM_BANNER) {
            const selectedTags = group.value.piTagsEditor
                ? mapPiTagsToApi(group.value.piTagsEditor).targetTags
                : undefined;

            acc[editorId] = {
                platforms: getTargetPlatformsData({
                    productsList,
                    selectedTags: selectedPlatforms && selectedPlatforms.length ? selectedPlatforms : selectedTags
                }),
                errors: []
            };
        }

        return acc;
    }, {});
}

function mapSmartGroupData(
    groupRow: adGroupsRowFragmentForEdit_GdSmartAdGroup,
    smartFilters: Array<EditGroupFilterItem>,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType>
): EditPerformanceGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);

    return {
        campaignType: CampaignType.PERFORMANCE,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        minusKeywords: groupRow.minusKeywords || [],
        selectedLibraryList: mapSelectedLibraryList(groupRow.libraryMinusKeywordsPacks),
        rateCorrections: mapRateCorrectionsData(groupRow.bidModifiers, availableBidModifiersTypes),
        hrefParams: groupRow.trackingParams || '',
        bannerHeadersFeedField: getValueFromApiFormat(groupRow.fieldToUseAsName) || '',
        bannerTextFeedField: getValueFromApiFormat(groupRow.fieldToUseAsBody) || '',
        feedsPopupIsOpen: false,
        searchFeedFieldValue: '',
        selectedFeedId: '',
        feedId: String(groupRow.feedId),
        filters: smartFilters || [],
        piTagsEditor: {}
    };
}

function mapInternalGroupData(
    groupRow: adGroupsRowFragmentForEdit_GdInternalAdGroup,
    regionTree: Region[],
    groupType: GdCampaignType
): EditInternalGroupDataValue {
    const type = mapCampaignRowTypeToCampaignType(groupType);
    const internalGroupType = getInternalType(type);

    if (!internalGroupType) {
        throw new Error('not internal group type');
    }

    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);

    return {
        campaignType: internalGroupType,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        name: groupRow.name,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        targetingYandexUids: groupRow.targetingYandexUids
            ? {
                  joinType: groupRow.targetingYandexUids.joinType,
                  targetingMode: groupRow.targetingYandexUids.targetingMode,
                  value: groupRow.targetingYandexUids.value || []
              }
            : undefined,
        targetingOsNames: groupRow.targetingOsNames
            ? {
                  joinType: groupRow.targetingOsNames.joinType,
                  targetingMode: groupRow.targetingOsNames.targetingMode,
                  value: groupRow.targetingOsNames.value || []
              }
            : undefined,
        targetingOsFamilies: groupRow.targetingOsFamilies
            ? {
                  joinType: groupRow.targetingOsFamilies.joinType,
                  targetingMode: groupRow.targetingOsFamilies.targetingMode,
                  value: groupRow.targetingOsFamilies.value.map(value => ({
                      targetingValueEntryId: value.targetingValueEntryId,
                      minVersion: mapVersionFromApiFormat(value.minVersion),
                      maxVersion: mapVersionFromApiFormat(value.maxVersion)
                  }))
              }
            : undefined,
        targetingBrowserNames: groupRow.targetingBrowserNames
            ? {
                  joinType: groupRow.targetingBrowserNames.joinType,
                  targetingMode: groupRow.targetingBrowserNames.targetingMode,
                  value: groupRow.targetingBrowserNames.value.map(value => ({
                      targetingValueEntryId: value.targetingValueEntryId,
                      minVersion: mapVersionFromApiFormat(value.minVersion),
                      maxVersion: mapVersionFromApiFormat(value.maxVersion)
                  }))
              }
            : undefined,
        targetingBrowserEngines: groupRow.targetingBrowserEngines
            ? {
                  joinType: groupRow.targetingBrowserEngines.joinType,
                  targetingMode: groupRow.targetingBrowserEngines.targetingMode,
                  value: groupRow.targetingBrowserEngines.value.map(value => ({
                      targetingValueEntryId: value.targetingValueEntryId,
                      minVersion: mapVersionFromApiFormat(value.minVersion),
                      maxVersion: mapVersionFromApiFormat(value.maxVersion)
                  }))
              }
            : undefined,
        targetingDeviceVendors: groupRow.targetingDeviceVendors
            ? {
                  joinType: groupRow.targetingDeviceVendors.joinType,
                  targetingMode: groupRow.targetingDeviceVendors.targetingMode,
                  value: groupRow.targetingDeviceVendors.value || []
              }
            : undefined,
        targetingDeviceNames: groupRow.targetingDeviceNames
            ? {
                  joinType: groupRow.targetingDeviceNames.joinType,
                  targetingMode: groupRow.targetingDeviceNames.targetingMode,
                  value: groupRow.targetingDeviceNames.value || []
              }
            : undefined,
        targetingInterfaceLangs: groupRow.targetingInterfaceLangs
            ? {
                  joinType: groupRow.targetingInterfaceLangs.joinType,
                  targetingMode: groupRow.targetingInterfaceLangs.targetingMode,
                  value: groupRow.targetingInterfaceLangs.value || []
              }
            : undefined,
        targetingQueryReferers: groupRow.targetingQueryReferers
            ? {
                  joinType: groupRow.targetingQueryReferers.joinType,
                  targetingMode: groupRow.targetingQueryReferers.targetingMode,
                  value: groupRow.targetingQueryReferers.value || []
              }
            : undefined,
        targetingUserAgents: groupRow.targetingUserAgents
            ? {
                  joinType: groupRow.targetingUserAgents.joinType,
                  targetingMode: groupRow.targetingUserAgents.targetingMode,
                  value: groupRow.targetingUserAgents.value || []
              }
            : undefined,
        targetingInternalNetwork: groupRow.targetingInternalNetwork
            ? {
                  joinType: groupRow.targetingInternalNetwork.joinType,
                  targetingMode: groupRow.targetingInternalNetwork.targetingMode
              }
            : undefined,
        targetingIsTablet: groupRow.targetingIsTablet
            ? {
                  joinType: groupRow.targetingIsTablet.joinType,
                  targetingMode: groupRow.targetingIsTablet.targetingMode
              }
            : undefined,
        targetingIsBrowser: groupRow.targetingIsBrowser
            ? {
                  joinType: groupRow.targetingIsBrowser.joinType,
                  targetingMode: groupRow.targetingIsBrowser.targetingMode
              }
            : undefined,
        targetingIsTouch: groupRow.targetingIsTouch
            ? {
                  joinType: groupRow.targetingIsTouch.joinType,
                  targetingMode: groupRow.targetingIsTouch.targetingMode
              }
            : undefined,
        targetingIsMobile: groupRow.targetingIsMobile
            ? {
                  joinType: groupRow.targetingIsMobile.joinType,
                  targetingMode: groupRow.targetingIsMobile.targetingMode
              }
            : undefined,
        targetingShowDates: groupRow.targetingShowDates
            ? {
                  joinType: groupRow.targetingShowDates.joinType,
                  targetingMode: groupRow.targetingShowDates.targetingMode,
                  value: groupRow.targetingShowDates.value
                      ? groupRow.targetingShowDates.value.sort((a, b) => moment(a).diff(moment(b)))
                      : []
              }
            : undefined,
        targetingClidTypes: groupRow.targetingClidTypes
            ? {
                  joinType: groupRow.targetingClidTypes.joinType,
                  targetingMode: groupRow.targetingClidTypes.targetingMode,
                  value: groupRow.targetingClidTypes.value || []
              }
            : undefined,
        targetingClids: groupRow.targetingClids
            ? {
                  joinType: groupRow.targetingClids.joinType,
                  targetingMode: groupRow.targetingClids.targetingMode,
                  value: groupRow.targetingClids.value || []
              }
            : undefined,
        targetingDesktopInstalledApps: groupRow.targetingDesktopInstalledApps
            ? {
                  joinType: groupRow.targetingDesktopInstalledApps.joinType,
                  targetingMode: groupRow.targetingDesktopInstalledApps.targetingMode,
                  value: groupRow.targetingDesktopInstalledApps.value || []
              }
            : undefined,
        targetingMobileInstalledApps: groupRow.targetingMobileInstalledApps
            ? {
                  joinType: groupRow.targetingMobileInstalledApps.joinType,
                  targetingMode: groupRow.targetingMobileInstalledApps.targetingMode,
                  value: groupRow.targetingMobileInstalledApps.value || []
              }
            : undefined,
        targetingQueryOptions: groupRow.targetingQueryOptions
            ? {
                  joinType: groupRow.targetingQueryOptions.joinType,
                  targetingMode: groupRow.targetingQueryOptions.targetingMode,
                  value: groupRow.targetingQueryOptions.value || []
              }
            : undefined,
        targetingTestIds: groupRow.targetingTestIds
            ? {
                  joinType: groupRow.targetingTestIds.joinType,
                  targetingMode: groupRow.targetingTestIds.targetingMode,
                  value: groupRow.targetingTestIds.value || []
              }
            : undefined,
        targetingYsCookies: groupRow.targetingYsCookies
            ? {
                  joinType: groupRow.targetingYsCookies.joinType,
                  targetingMode: groupRow.targetingYsCookies.targetingMode,
                  value: groupRow.targetingYsCookies.value || []
              }
            : undefined,
        targetingIsYandexPlus: groupRow.targetingIsYandexPlus
            ? {
                  joinType: groupRow.targetingIsYandexPlus.joinType,
                  targetingMode: groupRow.targetingIsYandexPlus.targetingMode
              }
            : undefined,
        targetingIsVirused: groupRow.targetingIsVirused
            ? {
                  joinType: groupRow.targetingIsVirused.joinType,
                  targetingMode: groupRow.targetingIsVirused.targetingMode
              }
            : undefined,
        startTime: getValueFromApiFormat(groupRow.startTime),
        finishTime: getValueFromApiFormat(groupRow.finishTime),
        level: groupRow.level || '0'
    };
}

function makeInternalGroupEmptyData(groupType: CampaignType): EditInternalGroupDataValue {
    const internalGroupType = getInternalType(groupType);
    if (!internalGroupType) {
        throw new Error('not internal group type');
    }

    return {
        campaignType: internalGroupType,
        groupType: GroupType.INTERNAL,
        name: i18n('newGroup'),
        regions: {
            selectedMap: {},
            regionIds: []
        },
        targetingYandexUids: undefined,
        targetingInternalNetwork: undefined,
        level: '1'
    };
}

function mapContentPromotionGroup(
    groupRow: adGroupsRowFragmentForEdit,
    showConditionsData: GroupShowConditionsData,
    regionTree: Region[],
    availableBidModifiersTypes: Array<GdBidModifierType> = [],
    generalRate?: number
): EditContentPromotionGroupDataValue {
    const selectedMap = RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, groupRow.regionsInfo.regionIds);

    return {
        name: groupRow.name,
        campaignType: CampaignType.CONTENT_PROMOTION,
        groupType: mapGroupRowTypeToGroupType(groupRow.type),
        autotargeting: showConditionsData.autotargeting,
        regions: {
            selectedMap: selectedMap,
            regionIds: groupRow.regionsInfo.regionIds
        },
        rateCorrections: mapRateCorrectionsData(groupRow.bidModifiers, availableBidModifiersTypes),
        minusKeywords: groupRow.minusKeywords,
        selectedLibraryList: mapSelectedLibraryList(groupRow.libraryMinusKeywordsPacks),
        keywords: showConditionsData.keywords,
        piTagsEditor: {},
        generalRate,
        availableGroups: [mapGroupRowTypeToGroupType(groupRow.type)]
    };
}

const mapSelectedLibraryList = (
    libraryMinusKeywordsPacks: adGroupsRowFragmentForEdit_GdContentPromotionAdGroup_libraryMinusKeywordsPacks[]
): MinusPhrasesShortLibraryItem[] => {
    return (
        libraryMinusKeywordsPacks.map(item => {
            return { id: String(item.id), name: item.name };
        }) || []
    );
};

function makeContentPromotionGroupEmptyData(
    availableAdGroupTypes: GdAdGroupType[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    regionTree: Region[],
    defaultRegionIds: RegionId[]
): EditContentPromotionGroupDataValue {
    const showConditionsData = getShowConditionsEmptyData();
    const rateCorrections = allowedRateCorrectionsData[GroupType.CONTENT_PROMOTION_VIDEO];

    return {
        name: i18n('newGroup'),
        campaignType: CampaignType.CONTENT_PROMOTION,
        groupType: mapGroupRowTypeToGroupType(availableAdGroupTypes[0]),
        autotargeting: showConditionsData.autotargeting,
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, defaultRegionIds),
            regionIds: defaultRegionIds
        },
        rateCorrections: mapRateCorrectionsData(null, rateCorrections),
        minusKeywords: [],
        selectedLibraryList: [],
        keywords: showConditionsData.keywords,
        piTagsEditor: {},
        generalRate: undefined,
        availableGroups: availableAdGroupTypes.map(mapGroupRowTypeToGroupType)
    };
}

function makePerformanceGroupEmptyData(
    regionTree: Region[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    defaultRegionIds: RegionId[]
): EditPerformanceGroupDataValue {
    const rateCorrections = allowedRateCorrectionsData[GroupType.PERFORMANCE];

    return {
        campaignType: CampaignType.PERFORMANCE,
        groupType: GroupType.PERFORMANCE,
        name: getNewGroupName(),
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, defaultRegionIds),
            regionIds: defaultRegionIds
        },
        minusKeywords: [],
        selectedLibraryList: [],
        rateCorrections: mapRateCorrectionsData(null, rateCorrections),
        hrefParams: '',
        filters: [],
        feedsPopupIsOpen: false,
        searchFeedFieldValue: '',
        selectedFeedId: '',
        feedId: '',
        bannerHeadersFeedField: '',
        bannerTextFeedField: '',
        piTagsEditor: {}
    };
}

function makeTextGroupEmptyData(
    regionTree: Region[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    defaultRegionIds: RegionId[],
    clientFeatures: ClientFeatures
): EditTextGroupDataValue {
    const rateCorrections = allowedRateCorrectionsData[GroupType.TEXT];
    const { isHonestAddTextAdGroupAllowed, relevanceMatchForNewGroupsEnabled } = clientFeatures;

    const isAutotargetingActive = Boolean(isHonestAddTextAdGroupAllowed && relevanceMatchForNewGroupsEnabled);

    return {
        campaignType: CampaignType.TEXT,
        groupType: GroupType.TEXT,
        name: getNewGroupName(),
        keywords: [],
        minusKeywords: [],
        selectedLibraryList: [],
        autotargeting: {
            isActive: isAutotargetingActive
        },
        retargeting: [],
        rateCorrections: mapRateCorrectionsData(null, rateCorrections),
        generalRate: undefined,
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, defaultRegionIds),
            regionIds: defaultRegionIds
        },
        audienceTargetings: {},
        audienceTargetingsTemplateName: getNewAudienceName(),
        piTagsEditor: {}
    };
}

function getCpmGroupDefaultRestrictionData(productsList: ProductsList, isOnlyMetro: boolean): string {
    if (isOnlyMetro) {
        const metroRestriction = getMetroRestriction(productsList);

        return metroRestriction.id;
    }

    const defaultRestrictionId = get(productsList, [CampaignType.CPM_BANNER, 'restrictions', 0, 'id']);

    if (defaultRestrictionId === undefined) {
        throw new Error('Для продукта CPM_BANNER нет данных');
    }

    return defaultRestrictionId;
}

function getCpmPriceGroupDefaultRestrictionData(productsList: ProductsList): string {
    const defaultRestrictionId = get(productsList, [CampaignType.CPM_PRICE, 'restrictions', 0, 'id']);
    if (defaultRestrictionId === undefined) {
        throw new Error('Для продукта CPM_PRICE нет данных');
    }

    return defaultRestrictionId;
}

function availableOnlyMetro(productsList: ProductsList): boolean {
    const restrictions: ProductsListItemRestriction[] = get(productsList, [CampaignType.CPM_BANNER, 'restrictions']);

    if (restrictions === undefined) {
        throw new Error('Для продукта CPM_BANNER нет данных');
    }

    const metroRestriction = restrictions.find(restriction => restriction.groupType === GroupType.CPM_GEOPRODUCT);
    const allActiveGroups = restrictions.every(restriction => restriction.active);

    return Boolean(metroRestriction && metroRestriction.active) && !allActiveGroups;
}

function getMetroRestriction(productsList: ProductsList): ProductsListItemRestriction {
    const restrictions: ProductsListItemRestriction[] = get(productsList, [CampaignType.CPM_BANNER, 'restrictions']);

    if (restrictions === undefined) {
        throw new Error('Для продукта CPM_BANNER нет данных');
    }

    const metroRestriction = restrictions.find(restriction => restriction.groupType === GroupType.CPM_GEOPRODUCT);

    if (!metroRestriction) {
        throw new Error('Отсутствует CPM_GEOPRODUCT restriction');
    }

    return metroRestriction;
}

function makeCpmGroupEmptyData(
    regionTree: Region[],
    defaultRegionIds: RegionId[],
    productsList: ProductsList
): EditCpmGroupDataValue {
    const isOnlyMetro = availableOnlyMetro(productsList);
    const productRestrictionId = getCpmGroupDefaultRestrictionData(productsList, isOnlyMetro);
    const { rateCurrentType, rateCorrections } = getCurrentRateTypeAndLists({
        productRestrictionId: productRestrictionId,
        rateCorrectionsFromApi: [],
        productsList: productsList
    });
    const showConditionsData = getShowConditionsEmptyData();
    const groupType = isOnlyMetro ? GroupType.CPM_GEOPRODUCT : GroupType.CPM_BANNER;

    return {
        name: getNewGroupName(),
        campaignType: CampaignType.CPM_BANNER,
        groupType,
        autotargeting: showConditionsData.autotargeting,
        keywords: showConditionsData.keywords,
        showConditionType: CpmGroupShowConditionType.USER_PROFILE,
        rateCurrentType,
        rateCorrections,
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, defaultRegionIds),
            regionIds: defaultRegionIds
        },
        outdoorMap: {
            pageBlocks: []
        },
        indoorMap: {
            pageBlocks: []
        },
        generalRate: undefined,
        selectedLibraryList: [],
        audienceTargetings: {},
        minusKeywords: [],
        productRestrictionId,
        audienceTargetingsTemplateName: '',
        piTagsEditor: {}
    };
}

function makeCpmPriceGroupEmptyData(
    regionTree: Region[],
    defaultRegionIds: RegionId[],
    productsList: ProductsList,
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    pricePackage?: PricePackagesForGroup
): EditCpmPriceGroupDataValue {
    const productRestrictionId = getCpmPriceGroupDefaultRestrictionData(productsList);
    const behavior = get(campaignData, 'flightTargetingsSnapshot.behaviors');
    const viewTypes = get(campaignData, 'flightTargetingsSnapshot.viewTypes');

    return {
        name: getNewGroupName(),
        campaignType: CampaignType.CPM_PRICE,
        groupType: GroupType.CPM_PRICE,
        regions: {
            selectedMap: RegionsHelpers.getSelectedMapFromRawRegionData(regionTree, defaultRegionIds),
            regionIds: defaultRegionIds
        },
        behaviors: behavior ? behavior.map(String) : [],
        defaultBehaviors: behavior ? behavior.map(String) : [],
        audienceTargetings: {},
        productRestrictionId,
        viewTypes,
        audienceTargetingsTemplateName: '',
        piTagsEditor: {},
        pricePackage,
        isDefault: !get(campaignData, 'hasDefaultAdGroup')
    };
}

const getShowConditionsData = (
    showConditionsData: GroupsQueryForEdit_client_showConditions_rowset[]
): Record<GroupId, GroupShowConditionsData> => {
    return showConditionsData.reduce((acc, item) => {
        if (acc[item.adGroupId] === undefined) {
            acc[item.adGroupId] = getShowConditionsEmptyData();
        }

        switch (item.__typename) {
            case 'GdKeyword':
                acc[item.adGroupId].keywords.push({
                    id: item.id,
                    phrase: item.keyword,
                    minusWords: item.minusKeywords
                });
                break;

            case 'GdRelevanceMatch':
                acc[item.adGroupId].autotargeting = {
                    id: item.id,
                    isActive: true
                };
                break;

            default:
                break;
        }

        return acc;
    }, {});
};

export const makeEmptySmartFilterItem = (campaign?: FeedFilterCampaign): EditGroupFilterItem => {
    return {
        autobudgetPriority: GdShowConditionAutobudgetPriority.MEDIUM,
        type: GdSmartFilterTargetFunnel.NEW_AUDITORY,
        name: '',
        id: '',
        tab: GdSmartFilterTab.ALL_PRODUCTS,
        conditions: [],
        feedCategories: [],
        vendors: '',
        priceRange: [0, 0],
        categoryIds: [],
        campaign: campaign || {},
        isAvailable: false
    };
};

export const mapAvailableConditionFromApiResponse = (conditions: Array<FeedFilterCondition>): boolean => {
    const availableCondition = conditions.find(condition => condition.field === 'available');

    return Boolean(availableCondition && availableCondition.arguments);
};

const mapVendorsConditionFromApiResponse = (conditions: Array<FeedFilterCondition>): string => {
    const vendorCondition = conditions.find(condition => condition.field === 'vendor');

    return (vendorCondition && vendorCondition.arguments.join(',')) || '';
};

const mapPriceConditionFromApiResponse = (conditions: Array<FeedFilterCondition>): NumberRangeInputValues => {
    const priceCondition = conditions.find(condition => condition.field === 'price');

    const priceRanges = priceCondition && (priceCondition.arguments as Array<NumberRangeInputValues>);

    return (priceRanges && priceRanges[0]) || [0, 0];
};

const mapCategoryIdConditionFromApiResponse = (conditions: Array<FeedFilterCondition>): Array<string> => {
    const categoryIdCondition = conditions.find(condition => condition.field === 'categoryId');

    return (categoryIdCondition ? categoryIdCondition.arguments : []) as Array<string>;
};

export const mapCrossTabConditionsFromApiResponse = (
    conditions: Array<FeedFilterCondition>,
    tab: GdSmartFilterTab
): {
    vendors: string;
    priceRange: NumberRangeInputValues;
    categoryIds: Array<string>;
    conditions: Array<FeedFilterCondition>;
} => {
    const CROSS_TAB_CONDITION_FIELDS = ['price', 'vendor', 'categoryId', 'available'];

    if (tab === GdSmartFilterTab.TREE) {
        return {
            vendors: mapVendorsConditionFromApiResponse(conditions),
            priceRange: mapPriceConditionFromApiResponse(conditions),
            categoryIds: mapCategoryIdConditionFromApiResponse(conditions),
            conditions: conditions.filter(condition => !CROSS_TAB_CONDITION_FIELDS.includes(condition.field))
        };
    }

    return {
        vendors: '',
        priceRange: [0, 0],
        categoryIds: [],
        conditions: conditions.filter(condition => condition.field !== 'available')
    };
};

const getGroupsFilterCampaignInfo = (
    groupsList: Array<GroupsQueryForEdit_client_adGroups_rowset>
): Record<GroupId, FeedFilterCampaign> => {
    return groupsList.reduce((acc, group) => {
        const { id, campaign } = group;
        const { strategyType: strategy, platform } = campaign.strategy;

        if (!acc[id]) {
            acc[id] = { strategy, platform };
        }

        return acc;
    }, {});
};

export const getSmartFiltersData = (
    smartFilterData: Array<GroupsQueryForEdit_client_smartFilters_rowset>,
    campaigns: Record<GroupId, FeedFilterCampaign>
): Record<GroupId, Array<EditGroupFilterItem>> => {
    const accumulator: Record<GroupId, Array<EditGroupFilterItem>> = {};

    return smartFilterData.reduce((acc, item) => {
        const { adGroupId, id, targetFunnel, name, priceCpa, priceCpc, autobudgetPriority, conditions, tab } = item;

        if (!acc[adGroupId]) {
            acc[adGroupId] = [];
        }

        const resultConditions = conditions.map(mapSmartFiltersConditionFromApiResponse);

        acc[adGroupId].push({
            ...mapCrossTabConditionsFromApiResponse(resultConditions, tab),
            isAvailable: mapAvailableConditionFromApiResponse(resultConditions),
            type: targetFunnel,
            id,
            name,
            priceCpa: Number(priceCpa) || undefined,
            priceCpc: Number(priceCpc) || undefined,
            autobudgetPriority: autobudgetPriority || GdShowConditionAutobudgetPriority.MEDIUM,
            feedCategories: [],
            tab,
            campaign: campaigns[adGroupId]
        });
        return acc;
    }, accumulator);
};

export const mapSmartFiltersConditionArgumentsFromApiResponse = (
    stringValue: string,
    field: string,
    operator: GdSmartFilterConditionOperator
): FeedFilterOperationControlValues => {
    const parsedValues = JSON.parse(stringValue);

    if (operator === GdSmartFilterConditionOperator.RANGE) {
        return parsedValues.map((item: string) => item.split('-').map(Number));
    }

    return parsedValues;
};

export const makeSmartFilterCondition = (
    field: string,
    operator: GdSmartFilterConditionOperator,
    values: FeedFilterOperationControlValues
): FeedFilterCondition => {
    return {
        id: uniqueId('smartFilterCondition'),
        field,
        arguments: values,
        operator
    };
};

export const mapSmartFiltersConditionFromApiResponse = (
    condition: GroupsQueryForEdit_client_smartFilters_rowset_conditions
): FeedFilterCondition => {
    const { field, operator, stringValue } = condition;
    const values = mapSmartFiltersConditionArgumentsFromApiResponse(stringValue, field, operator);

    return makeSmartFilterCondition(field, operator, values);
};

export interface RetargetingData {
    priceContext: number;
    retargetings: Array<RetargetingValue>;
}

export const getRetargetingData = (
    retargetingData: GroupsQueryForEdit_client_retargetings_rowset[]
): Record<GroupId, RetargetingData> => {
    return retargetingData.reduce(
        (acc: Record<GroupId, RetargetingData>, item: GroupsQueryForEdit_client_retargetings_rowset) => {
            if (acc[item.adGroupId] === undefined) {
                acc[item.adGroupId] = {
                    priceContext: Number(item.priceContext),
                    retargetings: []
                };
            }

            acc[item.adGroupId].retargetings.push({
                id: item.retargetingConditionId,
                retargetingId: item.retargetingId
            });

            return acc;
        },
        {}
    );
};

export const getUserProfileRetargetingData = (
    retargetingData?: ApolloQueryResult<RetargetingConditionsQueryForUserProfile>
): Record<GroupId, UserProfileRetargetingCondition> => {
    if (!retargetingData) {
        return {};
    }

    return retargetingData.data.client.retargetingConditions.rowset.reduce(
        (
            acc: Record<GroupId, UserProfileRetargetingCondition>,
            item: RetargetingConditionsQueryForUserProfile_client_retargetingConditions_rowset
        ) => {
            item.adGroupIds.forEach(adGroupId => {
                acc[adGroupId] = mapUserProfileRetargetingConditionFromApiFormat(item);
            });

            return acc;
        },
        {}
    );
};

function mapBaseGroupData(groupRow: adGroupsRowFragmentForEdit): EditBaseGroupDataValue {
    return {
        name: groupRow.name,
        campaignType: undefined,
        groupType: mapGroupRowTypeToGroupType(groupRow.type)
    };
}

export const mapGroupsForEditQueryResultToQueryResponse = (
    login: string,
    result: ApolloQueryResult<GroupsQueryForEdit>,
    regionTree: Region[],
    isDeviceModifiersAllowed: boolean,
    userProfileRetargetings?: ApolloQueryResult<RetargetingConditionsQueryForUserProfile>,
    productsList?: Record<string, ProductsListItem>,
    isTargetTagsAllowed?: boolean,
    regionTrimmedTrees?: Record<string, Region[]>
): DataForEditGroups => {
    const { client } = result.data;
    const {
        clientConstants: { allowedBidModifierTypesByAdGroupTypes, allowedBidModifiersValues, clientLimits },
        adGroups: groupsResult,
        showConditions,
        feeds: originFeedsData,
        retargetings,
        interests,
        smartFilters
    } = client;

    const { rowset: groupRows } = groupsResult;

    const showConditionsData = getShowConditionsData(showConditions.rowset);
    const campaignsData = getGroupsFilterCampaignInfo(groupRows);
    const smartFiltersData = getSmartFiltersData(smartFilters.rowset, campaignsData);
    const { feedsIds, feeds } = mapFeeds(originFeedsData.rowset);
    const retargetingData = getRetargetingData(retargetings.rowset);
    const interestsData = getRetargetingData(interests.rowset);
    const userProfileRetargetingData = getUserProfileRetargetingData(userProfileRetargetings);

    const allowedRateCorrectionsData = getAllowedRateCorrectionsDataForGroup(allowedBidModifierTypesByAdGroupTypes);

    const rateCorrectionsConstants = mapRatesCorrectionsConstantsFromApiFormat(
        allowedBidModifiersValues,
        isDeviceModifiersAllowed
    );

    const resultResponse = {
        reqId: result.data.reqId,
        groups: groupRows.reduce((acc: Record<string, EditGroupData>, groupRow: adGroupsRowFragmentForEdit): Record<
            string,
            EditGroupData
        > => {
            const groupId = groupRow.id;
            const groupShowConditionsData = showConditionsData[groupId] || getShowConditionsEmptyData();
            const { retargetings: groupRetargetingConditions = [] } = retargetingData[groupId] || {};
            const regionTrimmedTree =
                regionTrimmedTrees && regionTrimmedTrees[groupRow.campaign.id]
                    ? regionTrimmedTrees[groupRow.campaign.id]
                    : regionTree;

            const priceContext =
                (retargetingData[groupId] && retargetingData[groupId].priceContext) ||
                (interestsData[groupId] && interestsData[groupId].priceContext);

            const mainAd: adPreviewFragment | null = groupRow.mainAd;

            const mainBanner = mainAd
                ? mapRowGroupToBannerPreviewData(mainAd, groupRow.campaign.type, groupRow)
                : undefined;

            let groupValue: EditGroupDataValue;

            switch (groupRow.__typename) {
                case 'GdTextAdGroup':
                    groupValue = mapTextGroupData(
                        groupRow,
                        groupShowConditionsData,
                        groupRetargetingConditions,
                        regionTree,
                        allowedRateCorrectionsData[groupRow.type],
                        userProfileRetargetingData[groupId]
                    );
                    break;
                case 'GdMcBannerAdGroup':
                    groupValue = mapMcBannerGroupData(
                        groupRow,
                        groupShowConditionsData,
                        regionTree,
                        allowedRateCorrectionsData[groupRow.type]
                    );
                    break;
                case 'GdSmartAdGroup':
                    groupValue = mapSmartGroupData(
                        groupRow,
                        smartFiltersData[groupId],
                        regionTree,
                        allowedRateCorrectionsData[groupRow.type]
                    );
                    break;
                case 'GdInternalAdGroup':
                    groupValue = mapInternalGroupData(groupRow, regionTree, groupRow.campaign.type);
                    break;
                case 'GdCpmVideoAdGroup':
                case 'GdCpmBannerAdGroup':
                case 'GdCpmAudioAdGroup':
                case 'GdCpmIndoorAdGroup':
                case 'GdCpmOutdoorAdGroup':
                case 'GdCpmGeoproductAdGroup':
                    groupValue = mapCpmGroupData(
                        groupRow,
                        groupShowConditionsData,
                        regionTree,
                        allowedRateCorrectionsData[groupRow.type],
                        priceContext,
                        userProfileRetargetingData[groupId],
                        productsList
                    );
                    break;
                case 'GdCpmPriceAdGroup':
                    const pricePackage = mapPricePackagesDataForGroup(groupRow.campaign);

                    groupValue = mapCpmPriceGroupData(
                        groupRow,
                        regionTree,
                        userProfileRetargetingData[groupId],
                        pricePackage
                    );
                    break;
                case 'GdContentPromotionAdGroup':
                    groupValue = mapContentPromotionGroup(
                        groupRow,
                        groupShowConditionsData,
                        regionTrimmedTree,
                        allowedRateCorrectionsData[groupRow.type],
                        priceContext
                    );
                    break;
                default:
                    groupValue = mapBaseGroupData(groupRow);
            }

            groupValue.piTagsEditor = mapPiTagsFromApi({
                pageGroupTags: groupRow.pageGroupTags,
                targetTags: groupRow.targetTags
            });

            let campaignData: {};

            switch (groupRow.campaign.__typename) {
                case 'GdPriceCampaign':
                    campaignData = {
                        startDate: groupRow.campaign.startDate,
                        endDate: groupRow.campaign.endDate,
                        pricePackageId: groupRow.campaign.pricePackageId,
                        flightOrderVolume: groupRow.campaign.flightOrderVolume,
                        regionIds: groupRow.campaign.flightTargetingsSnapshot
                            ? groupRow.campaign.flightTargetingsSnapshot.geoExpanded
                            : undefined,
                        regionTrimmedTree: regionTrimmedTree
                    };
                    break;
                default:
                    campaignData = {};
                    break;
            }

            acc[groupId] = {
                id: String(groupId),
                serverValue: { ...groupValue },
                value: { ...groupValue },
                campaign: {
                    ...campaignData,
                    id: String(groupRow.campaign.id),
                    name: groupRow.campaign.name,
                    strategyType: groupRow.campaign.strategy.strategyType,
                    platform: groupRow.campaign.strategy.platform,
                    strategy: {
                        isAutoBudget: groupRow.campaign.strategy.isAutoBudget
                    }
                },
                filtersEditor: {
                    isOpen: false,
                    isLoading: false,
                    auxiliaryFilter: ''
                },
                hasGeneralRate: groupRow.access.showGeneralPriceOnEdit,
                isEditable: groupRow.access.canEdit,
                isSaved: false,
                errors: {},
                bannersCount: groupRow.adsCount,
                isNew: false,
                showConditionsRecommendationData: {
                    wasClosed: false
                },
                previewBanners: {
                    mainBanner,
                    isBannersPopupVisible: false
                }
            };

            return acc;
        }, {}),
        feedsIds,
        feeds,
        isLibraryListsFetched: false,
        allowedRateCorrectionsData,
        rateCorrectionsConstants,
        keywordsCountLimit: Number(clientLimits.keywordsCountLimit)
    };

    return resultResponse;
};

export const mapRetargetingConditionsQueryResultToQueryResponse = (
    login: string,
    result: ApolloQueryResult<RetargetingConditionsQueryForGroupEdit>
): UpdateRetargetingConditionsForGroupsEdit => {
    const { client } = result.data;
    const { retargetingConditions } = client;

    return {
        retargetingConditions: retargetingConditions.rowset.map(retargetingCondition =>
            mapRetargetingConditionFromApiFormat(retargetingCondition, login)
        )
    };
};

export const mapFeedCategory = (category: GroupsQueryForEdit_client_feeds_rowset_categories): FeedCategory => {
    const { categoryId, parentCategoryId, isDeleted, name, offerCount } = category;

    return {
        categoryId,
        parentCategoryId,
        isDeleted,
        name,
        offerCount
    };
};

export const mapFeeds = (
    originFeeds: GroupsQueryForEdit_client_feeds_rowset[]
): {
    feedsIds: string[];
    feeds: Record<string, Feed>;
} => {
    const feedsIds: string[] = [];
    const feeds: Feeds = Object.create(null);

    originFeeds.forEach(feed => {
        const feedId = String(feed.id);

        feedsIds.push(feedId);
        feeds[feedId] = {
            id: feedId,
            name: feed.name,
            updateStatus: feed.updateStatus,
            businessType: feed.businessType,
            feedType: getValueFromApiFormat(feed.feedType),
            categories: feed.categories.map(mapFeedCategory)
        };
    });

    return {
        feedsIds,
        feeds
    };
};

export const makeGroupsForEditNewGroupData = (
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    regionTree: Region[],
    retargetingConditions: RetargetingConditionData[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    rateCorrectionsConstants: RatesCorrectionsConstantsData,
    keywordsCountLimit: string,
    clientFeatures: ClientFeatures
): DataForEditGroups => {
    let groupInnerValue: EditGroupDataValue;

    switch (CampaignType[campaignData.type]) {
        case CampaignType.PERFORMANCE:
            groupInnerValue = makePerformanceGroupEmptyData(
                regionTree,
                allowedRateCorrectionsData,
                mapDefaultRegionIds(campaignData.defaultRegionIds)
            );
            break;
        case CampaignType.INTERNAL_FREE:
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_AUTOBUDGET:
            const type = mapCampaignRowTypeToCampaignType(campaignData.type);
            groupInnerValue = makeInternalGroupEmptyData(type);
            break;
        case CampaignType.CONTENT_PROMOTION:
            groupInnerValue = makeContentPromotionGroupEmptyData(
                campaignData.availableAdGroupTypes,
                allowedRateCorrectionsData,
                regionTree,
                mapDefaultRegionIds(campaignData.defaultRegionIds)
            );
            break;
        case CampaignType.TEXT:
            groupInnerValue = makeTextGroupEmptyData(
                regionTree,
                allowedRateCorrectionsData,
                mapDefaultRegionIds(campaignData.defaultRegionIds),
                clientFeatures
            );
            break;
        default:
            throw new Error(`создание групп типа ${campaignData.type} не поддерживается!`);
    }

    const dataForEditGroups: DataForEditGroups = makeNewDataForEditGroups(
        groupInnerValue,
        campaignData,
        keywordsCountLimit,
        allowedRateCorrectionsData,
        rateCorrectionsConstants,
        true
    );

    if (CampaignType[campaignData.type] === CampaignType.PERFORMANCE) {
        dataForEditGroups.retargetingConditions = retargetingConditions;
        dataForEditGroups.allowedRateCorrectionsData = allowedRateCorrectionsData;
        dataForEditGroups.rateCorrectionsConstants = rateCorrectionsConstants;
    }

    if (CampaignType[campaignData.type] === CampaignType.TEXT) {
        dataForEditGroups.retargetingConditions = retargetingConditions;
    }

    return dataForEditGroups;
};

export const makeGroupsForEditNewCpmGroupData = (
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    regionTree: Region[],
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    keywordsCountLimit: string,
    productsList: ProductsList,
    rateCorrectionsConstants: RatesCorrectionsConstantsData,
    isTargetTagsAllowed?: boolean,
    pricePackage?: PricePackagesForGroup,
    regionTrimmedTree?: Region[]
): DataForEditGroups => {
    let groupInnerValue: EditGroupDataValue;

    if (campaignData.type === GdCampaignType.CPM_PRICE) {
        const geoExpanded = get(campaignData, 'flightTargetingsSnapshot.geoExpanded');
        const defaultRegionIds = geoExpanded && geoExpanded.length > 0 ? geoExpanded : campaignData.defaultRegionIds;

        groupInnerValue = makeCpmPriceGroupEmptyData(
            regionTree,
            mapDefaultRegionIds(defaultRegionIds),
            productsList,
            campaignData,
            pricePackage
        );
    } else {
        groupInnerValue = makeCpmGroupEmptyData(
            regionTree,
            mapDefaultRegionIds(campaignData.defaultRegionIds),
            productsList
        );
    }

    return makeNewDataForEditGroups(
        groupInnerValue,
        campaignData,
        keywordsCountLimit,
        allowedRateCorrectionsData,
        rateCorrectionsConstants,
        true,
        regionTrimmedTree
    );
};

export const makeNewDataForEditGroups = (
    groupInnerValue: EditGroupDataValue,
    campaignData: CampaignBasicDataForGroupEdit_client_campaigns_rowset,
    keywordsCountLimit: string,
    allowedRateCorrectionsData: Record<GroupType, Array<GdBidModifierType>>,
    rateCorrectionsConstants: RatesCorrectionsConstantsData,
    hasGeneralRate?: boolean,
    regionTrimmedTree?: Region[]
): DataForEditGroups => {
    const groupId = `${campaignData.id}${new Date().getTime()}`;

    let campaignValue: {};

    switch (campaignData.__typename) {
        case 'GdPriceCampaign':
            const geoExpanded = campaignData.flightTargetingsSnapshot
                ? campaignData.flightTargetingsSnapshot.geoExpanded
                : undefined;
            const defaultRegionIds =
                geoExpanded && geoExpanded.length > 0 ? geoExpanded : campaignData.defaultRegionIds;

            campaignValue = {
                startDate: campaignData.startDate,
                endDate: campaignData.endDate,
                pricePackageId: campaignData.pricePackageId,
                flightOrderVolume: campaignData.flightOrderVolume,
                regionTrimmedTree: regionTrimmedTree,
                regionIds: defaultRegionIds
            };
            break;
        default:
            campaignValue = {};
            break;
    }

    return {
        groups: {
            [groupId]: {
                id: groupId,
                serverValue: { ...groupInnerValue },
                value: { ...groupInnerValue },
                hasGeneralRate: Boolean(hasGeneralRate),
                isSaved: false,
                isEditable: true,
                errors: {},
                bannersCount: 0,
                campaign: {
                    ...campaignValue,
                    id: campaignData.id,
                    name: campaignData.name,
                    strategyType: campaignData.strategy.strategyType,
                    strategy: {
                        isAutoBudget: campaignData.strategy.isAutoBudget
                    }
                },
                filtersEditor: {
                    isOpen: false,
                    isLoading: false,
                    auxiliaryFilter: ''
                },
                isNew: true,
                showConditionsRecommendationData: {
                    wasClosed: false
                },
                previewBanners: {
                    isBannersPopupVisible: false
                }
            }
        },
        feedsIds: [],
        feeds: {},
        isLibraryListsFetched: false,
        rateCorrectionsConstants,
        allowedRateCorrectionsData,
        keywordsCountLimit: Number(keywordsCountLimit)
    };
};

const mapDefaultRegionIds = (defaultRegionIds: (string | null)[]): RegionId[] => {
    return defaultRegionIds.filter(regionId => regionId !== null).map(regionId => Number(regionId));
};

export const mapGroupToTextInput = (
    group: EditGroupData,
    cryptaSegmentsByType: CryptaSegmentsByType,
    isNewGroup: boolean
): GdUpdateTextAdGroupItemInput => {
    const { value } = group;

    switch (value.campaignType) {
        case CampaignType.TEXT:
            const groupAutotargeting = value.autotargeting;
            const groupId = group.id;
            const campaignId = group.campaign.id;
            const piTagsEditor = value.piTagsEditor;
            const tags = mapPiTagsToApi(piTagsEditor);

            return {
                adGroupId: group.id,
                regionIds: value.regions.regionIds,
                adGroupName: value.name,
                adGroupMinusKeywords: value.minusKeywords,
                keywords: mapKeywordsToApi(value.keywords),
                relevanceMatch: mapRelevanceMatchToApi(groupAutotargeting),
                retargetings: value.retargeting.map(retargetingValue => {
                    return {
                        retCondId: retargetingValue.id,
                        id: retargetingValue.retargetingId || null
                    };
                }),
                retargetingCondition: mapAudienceTargetingsToRetargetingConditionsApiFormat({
                    audienceTargetings: value.audienceTargetings,
                    audienceTargetingsTemplateName: value.audienceTargetingsTemplateName,
                    cryptaSegmentsByType
                }),
                useBidModifiers: true,
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    value.rateCorrections,
                    groupId,
                    campaignId,
                    isNewGroup
                ),
                generalPrice: mapGeneralRateToApi(value.generalRate),
                libraryMinusKeywordsIds: (value.selectedLibraryList || []).map(list => list.id),
                pageGroupTags: tags.pageGroupTags,
                targetTags: tags.targetTags
            };
        default:
            throw new Error('unsupported group type');
    }
};

export const mapGroupToMcBannerInput = (group: EditGroupData): GdUpdateMcBannerAdGroupItemInput => {
    const { value } = group;

    switch (value.campaignType) {
        case CampaignType.MCBANNER:
            const groupId = group.id;
            const campaignId = group.campaign.id;
            const piTagsEditor = value.piTagsEditor;
            const tags = mapPiTagsToApi(piTagsEditor);

            return {
                adGroupId: groupId,
                regionIds: value.regions.regionIds,
                adGroupName: value.name,
                adGroupMinusKeywords: value.minusKeywords,
                keywords: mapKeywordsToApi(value.keywords),
                useBidModifiers: true,
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    value.rateCorrections,
                    groupId,
                    campaignId,
                    false
                ),
                generalPrice: mapGeneralRateToApi(value.generalRate),
                libraryMinusKeywordsIds: (value.selectedLibraryList || []).map(list => list.id),
                pageGroupTags: tags.pageGroupTags,
                targetTags: tags.targetTags
            };
        default:
            throw new Error('unsupported group type');
    }
};

export const mapAddGroupToContentPromotionInput = (group: EditGroupData): GdAddContentPromotionAdGroupItemInput => {
    const { value } = group;

    if (value.campaignType !== CampaignType.CONTENT_PROMOTION) {
        throw new Error('unsupported group type');
    }

    const groupId = group.id;
    const campaignId = group.campaign.id;
    const groupAutotargeting = value.autotargeting;
    const piTagsEditor = value.piTagsEditor;
    const tags = mapPiTagsToApi(piTagsEditor);

    return {
        campaignId: campaignId,
        name: value.name,
        contentPromotionGroupType: mapContentPromotionGroupTypeToApiFormat(value.groupType),
        adGroupMinusKeywords: value.minusKeywords,
        generalPrice: mapGeneralRateToApi(value.generalRate),
        pageGroupTags: tags.pageGroupTags,
        targetTags: tags.targetTags,
        bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(value.rateCorrections, groupId, campaignId, true),
        libraryMinusKeywordsIds: mapLibraryMinusKeywordsIdsToApi(value.selectedLibraryList),
        useBidModifiers: true,
        relevanceMatch: mapRelevanceMatchToApi(groupAutotargeting),
        regionIds: value.regions.regionIds,
        keywords: mapKeywordsToApi(value.keywords)
    };
};

const mapLibraryMinusKeywordsIdsToApi = (selectedLibraryList?: MinusPhrasesShortLibraryItem[]): string[] => {
    return (selectedLibraryList || []).map(list => list.id);
};

const mapGeneralRateToApi = (generalRate?: number): string | null => {
    return generalRate ? generalRate.toString() : null;
};

const mapRelevanceMatchToApi = (groupAutotargeting: EditGroupAutotargeting): GdUpdateAdGroupRelevanceMatchItemInput => {
    return groupAutotargeting.isActive
        ? {
              isActive: true,
              id: groupAutotargeting.id || null
          }
        : {
              isActive: false
          };
};

const mapKeywordsToApi = (keywords?: KeywordsData[]): GdUpdateAdGroupKeywordItemInput[] | null => {
    return keywords
        ? keywords.map(({ id, phrase, minusWords }) => ({
              id: id,
              phrase: mapPhraseWithMinusWordsToString(phrase, minusWords)
          }))
        : null;
};

const mapContentPromotionGroupTypeToApiFormat = (groupType: GroupType): GdContentPromotionGroupTypeInput => {
    switch (groupType) {
        case GroupType.CONTENT_PROMOTION_COLLECTION:
            return GdContentPromotionGroupTypeInput.COLLECTION;
        case GroupType.CONTENT_PROMOTION_VIDEO:
            return GdContentPromotionGroupTypeInput.VIDEO;
        default:
            throw new Error('Unsupported api Content Promotion group type');
    }
};

export const mapUpdateGroupToContentPromotionInput = (
    group: EditGroupData
): GdUpdateContentPromotionAdGroupItemInput => {
    const { value } = group;

    if (value.campaignType !== CampaignType.CONTENT_PROMOTION) {
        throw new Error('Unsupported Content Promotion group type');
    }

    const groupId = group.id;
    const campaignId = group.campaign.id;
    const groupAutotargeting = value.autotargeting;
    const piTagsEditor = value.piTagsEditor;
    const tags = mapPiTagsToApi(piTagsEditor);

    return {
        adGroupId: groupId,
        adGroupName: value.name,
        adGroupMinusKeywords: value.minusKeywords,
        generalPrice: mapGeneralRateToApi(value.generalRate),
        pageGroupTags: tags.pageGroupTags,
        targetTags: tags.targetTags,
        bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(value.rateCorrections, groupId, campaignId, false),
        libraryMinusKeywordsIds: mapLibraryMinusKeywordsIdsToApi(value.selectedLibraryList),
        useBidModifiers: true,
        relevanceMatch: mapRelevanceMatchToApi(groupAutotargeting),
        regionIds: value.regions.regionIds,
        keywords: mapKeywordsToApi(value.keywords)
    };
};

const mapGroupToCpmInput = (
    group: EditGroupData,
    productsList: ProductsList,
    cryptaSegmentsByType: CryptaSegmentsByType
): GdUpdateCpmAdGroupItemInput => {
    const {
        id: groupId,
        campaign: { id: campaignId },
        isNew,
        value: { piTagsEditor }
    } = group;
    const tags = mapPiTagsToApi(piTagsEditor);

    switch (group.value.campaignType) {
        case CampaignType.CPM_BANNER:
            const { value: valueCpmBanner } = group;

            const { showConditionType, audienceCollection = [] } = valueCpmBanner;
            const selectedRestriction = productsList[GroupType.CPM_BANNER].restrictions.find(restriction => {
                return restriction.id === valueCpmBanner.productRestrictionId;
            });

            const groupTypeBanner = selectedRestriction ? selectedRestriction.groupType : GroupType.CPM_BANNER;

            const hasShowConditionType = ![GroupType.CPM_INDOOR, GroupType.CPM_OUTDOOR].includes(groupTypeBanner);
            const isUserProfile = showConditionType === CpmGroupShowConditionType.USER_PROFILE;
            const isKeywords = showConditionType === CpmGroupShowConditionType.KEYWORD;
            const groupMap = {
                [GroupType.CPM_INDOOR]: valueCpmBanner.indoorMap,
                [GroupType.CPM_OUTDOOR]: valueCpmBanner.outdoorMap
            }[groupTypeBanner];

            const cpmGroupInput: GdUpdateCpmAdGroupItemInput = {
                type: GdCpmGroupTypeInput[groupTypeBanner],
                campaignId: isNew ? campaignId : null,
                adGroupId: isNew ? null : groupId,
                regionIds: hasShowConditionType ? valueCpmBanner.regions.regionIds : [],
                adGroupName: valueCpmBanner.name,
                videoGoals: audienceCollection.map(makeAudienceCollectionForApi),
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    valueCpmBanner.rateCorrections,
                    groupId,
                    campaignId,
                    isNew
                ),
                keywords:
                    hasShowConditionType && isKeywords
                        ? (valueCpmBanner.keywords || []).map(({ id, phrase, minusWords }) => ({
                              id: id,
                              phrase: mapPhraseWithMinusWordsToString(phrase, minusWords)
                          }))
                        : null,
                retargetingCondition:
                    hasShowConditionType && isUserProfile
                        ? mapAudienceTargetingsToRetargetingConditionsApiFormat({
                              audienceTargetings: valueCpmBanner.audienceTargetings,
                              audienceTargetingsTemplateName: valueCpmBanner.audienceTargetingsTemplateName,
                              cryptaSegmentsByType
                          })
                        : null,
                generalPrice: mapGeneralRateToApi(valueCpmBanner.generalRate),
                pageBlocks: groupMap ? groupMap.pageBlocks : null,
                pageGroupTags: tags.pageGroupTags,
                targetTags: tags.targetTags
            };
            const availibleGroupType: Set<GroupType> = new Set([GroupType.CPM_VIDEO, GroupType.CPM_AUDIO]);

            if (availibleGroupType.has(groupTypeBanner)) {
                cpmGroupInput.videoGoals = audienceCollection.map(makeAudienceCollectionForApi);
            }

            return cpmGroupInput;
        case CampaignType.CPM_PRICE:
            const { value: valueCpmPrice } = group;

            const behaviors = valueCpmPrice.behaviors;
            const selectedRestrictionGroupPrice = productsList[GroupType.CPM_PRICE].restrictions.find(restriction => {
                return restriction.id === valueCpmPrice.productRestrictionId;
            });

            const groupPrice = selectedRestrictionGroupPrice
                ? selectedRestrictionGroupPrice.groupType
                : GroupType.CPM_PRICE;

            if (behaviors && behaviors.length > 0) {
                valueCpmPrice.audienceTargetings[AudienceTargetingsType.behaviors] = behaviors;

                valueCpmPrice.audienceTargetingsTemplateName = valueCpmPrice.audienceTargetingsTemplateName
                    ? valueCpmPrice.audienceTargetingsTemplateName
                    : i18n('newAudience');
            }
            const retargetings = mapAudienceTargetingsToRetargetingConditionsApiFormat({
                audienceTargetings: valueCpmPrice.audienceTargetings,
                audienceTargetingsTemplateName: valueCpmPrice.audienceTargetingsTemplateName,
                cryptaSegmentsByType
            });

            const cpmPriceGroupInput: GdUpdateCpmAdGroupItemInput = {
                type: GdCpmGroupTypeInput[groupPrice],
                campaignId: isNew ? campaignId : null,
                adGroupId: isNew ? null : groupId,
                regionIds: valueCpmPrice.regions.regionIds,
                adGroupName: valueCpmPrice.name,
                retargetingCondition: retargetings.conditionRules.length > 0 ? retargetings : undefined,
                pageGroupTags: tags.pageGroupTags,
                targetTags: tags.targetTags,
                priority: valueCpmPrice.isDefault ? GROUP_PRIORITY.DEFAULT : GROUP_PRIORITY.SPECIFIC
            };

            return cpmPriceGroupInput;
        default:
            throw new Error('unsupported group type');
    }
};

export const mapGroupToSmartInput = (group: EditGroupData): GdUpdatePerformanceAdGroupItemInput => {
    const { value } = group;

    switch (value.campaignType) {
        case CampaignType.PERFORMANCE:
            const groupId = group.id;
            const campaignId = group.campaign.id;
            const piTagsEditor = value.piTagsEditor;
            const tags = mapPiTagsToApi(piTagsEditor);

            return {
                id: group.id,
                regionIds: value.regions.regionIds,
                trackingParams: value.hrefParams ? value.hrefParams : null,
                fieldToUseAsBody: value.bannerTextFeedField || null,
                fieldToUseAsName: value.bannerHeadersFeedField || null,
                minusKeywords: value.minusKeywords,
                libraryMinusKeywordsIds: mapLibraryMinusKeywordsIdsToApi(value.selectedLibraryList),
                name: value.name,
                bidModifiers: mapRatesCorrectionsValueToApiFormatForGroup(
                    value.rateCorrections,
                    groupId,
                    campaignId,
                    false
                ),
                pageGroupTags: tags.pageGroupTags,
                targetTags: tags.targetTags
            };
        default:
            throw new Error('unsupported group type');
    }
};

const mapTargetingShowDatesToApiFormat = (showDates?: TargetingShowDates): ApiFormatTargetingShowDates | undefined => {
    if (!showDates) {
        return undefined;
    }

    const joinType = mapJoinTypeToApiFormat(showDates.joinType);
    const targetingMode = mapTargetingModeToApiFormat(showDates.targetingMode);
    const value = showDates.value.reduce((acc: string[], val: string) => {
        const date = val.trim();
        if (!date || date === 'Invalid date') {
            return acc;
        }

        return [...acc, date];
    }, []);

    if (value.length === 0) {
        return undefined;
    }

    return {
        joinType,
        targetingMode,
        value
    };
};

export const mapGroupToInternalInput = (group: EditGroupData): GdUpdateInternalAdGroupsItemInput => {
    const { value } = group;
    switch (value.campaignType) {
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
        case CampaignType.INTERNAL_AUTOBUDGET:
            return {
                id: group.id,
                regionIds: value.regions.regionIds,
                name: value.name,
                level: value.level,
                targetingInternalNetwork: value.targetingInternalNetwork && {
                    joinType: mapJoinTypeToApiFormat(value.targetingInternalNetwork.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingInternalNetwork.targetingMode)
                },
                targetingIsBrowser: value.targetingIsBrowser && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsBrowser.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsBrowser.targetingMode)
                },
                targetingIsMobile: value.targetingIsMobile && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsMobile.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsMobile.targetingMode)
                },
                targetingIsTouch: value.targetingIsTouch && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsTouch.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsTouch.targetingMode)
                },
                targetingIsTablet: value.targetingIsTablet && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsTablet.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsTablet.targetingMode)
                },
                targetingYandexUids: value.targetingYandexUids && {
                    value: value.targetingYandexUids.value.map(uid => uid.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingYandexUids.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingYandexUids.targetingMode)
                },
                targetingOsNames: value.targetingOsNames && {
                    value: value.targetingOsNames.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingOsNames.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingOsNames.targetingMode)
                },
                targetingOsFamilies: value.targetingOsFamilies && {
                    value: value.targetingOsFamilies.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId,
                        minVersion: mapVersionToApiFormat(it.minVersion),
                        maxVersion: mapVersionToApiFormat(it.maxVersion)
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingOsFamilies.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingOsFamilies.targetingMode)
                },
                targetingBrowserNames: value.targetingBrowserNames && {
                    value: value.targetingBrowserNames.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId,
                        minVersion: mapVersionToApiFormat(it.minVersion),
                        maxVersion: mapVersionToApiFormat(it.maxVersion)
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingBrowserNames.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingBrowserNames.targetingMode)
                },
                targetingBrowserEngines: value.targetingBrowserEngines && {
                    value: value.targetingBrowserEngines.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId,
                        minVersion: mapVersionToApiFormat(it.minVersion),
                        maxVersion: mapVersionToApiFormat(it.maxVersion)
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingBrowserEngines.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingBrowserEngines.targetingMode)
                },
                targetingDeviceVendors: value.targetingDeviceVendors && {
                    value: value.targetingDeviceVendors.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingDeviceVendors.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingDeviceVendors.targetingMode)
                },
                targetingInterfaceLangs: value.targetingInterfaceLangs && {
                    value: mapInterfaceLangToApiFormat(value.targetingInterfaceLangs.value),
                    joinType: mapJoinTypeToApiFormat(value.targetingInterfaceLangs.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingInterfaceLangs.targetingMode)
                },
                targetingDeviceNames: value.targetingDeviceNames && {
                    value: value.targetingDeviceNames.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingDeviceNames.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingDeviceNames.targetingMode)
                },
                targetingQueryReferers: value.targetingQueryReferers && {
                    value: value.targetingQueryReferers.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingQueryReferers.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingQueryReferers.targetingMode)
                },
                targetingUserAgents: value.targetingUserAgents && {
                    value: value.targetingUserAgents.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingUserAgents.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingUserAgents.targetingMode)
                },
                targetingShowDates: mapTargetingShowDatesToApiFormat(value.targetingShowDates),
                targetingClidTypes: value.targetingClidTypes && {
                    value: value.targetingClidTypes.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingClidTypes.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingClidTypes.targetingMode)
                },
                targetingClids: value.targetingClids && {
                    value: value.targetingClids.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingClids.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingClids.targetingMode)
                },
                targetingDesktopInstalledApps: value.targetingDesktopInstalledApps && {
                    value: value.targetingDesktopInstalledApps.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingDesktopInstalledApps.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingDesktopInstalledApps.targetingMode)
                },
                targetingMobileInstalledApps: value.targetingMobileInstalledApps && {
                    value: value.targetingMobileInstalledApps.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingMobileInstalledApps.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingMobileInstalledApps.targetingMode)
                },
                targetingQueryOptions: value.targetingQueryOptions && {
                    value: value.targetingQueryOptions.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingQueryOptions.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingQueryOptions.targetingMode)
                },
                targetingTestIds: value.targetingTestIds && {
                    value: value.targetingTestIds.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingTestIds.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingTestIds.targetingMode)
                },
                targetingYsCookies: value.targetingYsCookies && {
                    value: value.targetingYsCookies.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingYsCookies.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingYsCookies.targetingMode)
                },
                targetingIsYandexPlus: value.targetingIsYandexPlus && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsYandexPlus.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsYandexPlus.targetingMode)
                },
                targetingIsVirused: value.targetingIsVirused && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsVirused.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsVirused.targetingMode)
                },
                startTime: value.startTime,
                finishTime: value.finishTime
            };
        default:
            throw new Error('unsupported group type');
    }
};

export const mapNewGroupToInternalInput = (group: EditGroupData): GdAddInternalAdGroupsItemInput => {
    const {
        value,
        campaign: { id }
    } = group;
    switch (value.campaignType) {
        case CampaignType.INTERNAL_DISTRIB:
        case CampaignType.INTERNAL_FREE:
        case CampaignType.INTERNAL_AUTOBUDGET:
            return {
                campaignId: id,
                regionIds: value.regions.regionIds,
                name: value.name,
                level: value.level,
                targetingInternalNetwork: value.targetingInternalNetwork && {
                    joinType: mapJoinTypeToApiFormat(value.targetingInternalNetwork.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingInternalNetwork.targetingMode)
                },
                targetingIsBrowser: value.targetingIsBrowser && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsBrowser.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsBrowser.targetingMode)
                },
                targetingIsMobile: value.targetingIsMobile && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsMobile.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsMobile.targetingMode)
                },
                targetingIsTouch: value.targetingIsTouch && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsTouch.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsTouch.targetingMode)
                },
                targetingIsTablet: value.targetingIsTablet && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsTablet.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsTablet.targetingMode)
                },
                targetingYandexUids: value.targetingYandexUids && {
                    value: value.targetingYandexUids.value.map(uid => uid.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingYandexUids.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingYandexUids.targetingMode)
                },
                targetingOsNames: value.targetingOsNames && {
                    value: value.targetingOsNames.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingOsNames.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingOsNames.targetingMode)
                },
                targetingOsFamilies: value.targetingOsFamilies && {
                    value: value.targetingOsFamilies.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId,
                        minVersion: mapVersionToApiFormat(it.minVersion),
                        maxVersion: mapVersionToApiFormat(it.maxVersion)
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingOsFamilies.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingOsFamilies.targetingMode)
                },
                targetingBrowserNames: value.targetingBrowserNames && {
                    value: value.targetingBrowserNames.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId,
                        minVersion: mapVersionToApiFormat(it.minVersion),
                        maxVersion: mapVersionToApiFormat(it.maxVersion)
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingBrowserNames.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingBrowserNames.targetingMode)
                },
                targetingBrowserEngines: value.targetingBrowserEngines && {
                    value: value.targetingBrowserEngines.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId,
                        minVersion: mapVersionToApiFormat(it.minVersion),
                        maxVersion: mapVersionToApiFormat(it.maxVersion)
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingBrowserEngines.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingBrowserEngines.targetingMode)
                },
                targetingDeviceVendors: value.targetingDeviceVendors && {
                    value: value.targetingDeviceVendors.value.map(it => ({
                        targetingValueEntryId: it.targetingValueEntryId
                    })),
                    joinType: mapJoinTypeToApiFormat(value.targetingDeviceVendors.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingDeviceVendors.targetingMode)
                },
                targetingInterfaceLangs: value.targetingInterfaceLangs && {
                    value: mapInterfaceLangToApiFormat(value.targetingInterfaceLangs.value),
                    joinType: mapJoinTypeToApiFormat(value.targetingInterfaceLangs.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingInterfaceLangs.targetingMode)
                },
                targetingDeviceNames: value.targetingDeviceNames && {
                    value: value.targetingDeviceNames.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingDeviceNames.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingDeviceNames.targetingMode)
                },
                targetingQueryReferers: value.targetingQueryReferers && {
                    value: value.targetingQueryReferers.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingQueryReferers.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingQueryReferers.targetingMode)
                },
                targetingUserAgents: value.targetingUserAgents && {
                    value: value.targetingUserAgents.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingUserAgents.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingUserAgents.targetingMode)
                },
                targetingShowDates: mapTargetingShowDatesToApiFormat(value.targetingShowDates),
                targetingClidTypes: value.targetingClidTypes && {
                    value: value.targetingClidTypes.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingClidTypes.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingClidTypes.targetingMode)
                },
                targetingClids: value.targetingClids && {
                    value: value.targetingClids.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingClids.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingClids.targetingMode)
                },
                targetingDesktopInstalledApps: value.targetingDesktopInstalledApps && {
                    value: value.targetingDesktopInstalledApps.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingDesktopInstalledApps.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingDesktopInstalledApps.targetingMode)
                },
                targetingMobileInstalledApps: value.targetingMobileInstalledApps && {
                    value: value.targetingMobileInstalledApps.value.map(it => it.trim()),
                    joinType: mapJoinTypeToApiFormat(value.targetingMobileInstalledApps.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingMobileInstalledApps.targetingMode)
                },
                targetingQueryOptions: value.targetingQueryOptions && {
                    value: value.targetingQueryOptions.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingQueryOptions.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingQueryOptions.targetingMode)
                },
                targetingTestIds: value.targetingTestIds && {
                    value: value.targetingTestIds.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingTestIds.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingTestIds.targetingMode)
                },
                targetingYsCookies: value.targetingYsCookies && {
                    value: value.targetingYsCookies.value,
                    joinType: mapJoinTypeToApiFormat(value.targetingYsCookies.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingYsCookies.targetingMode)
                },
                targetingIsYandexPlus: value.targetingIsYandexPlus && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsYandexPlus.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsYandexPlus.targetingMode)
                },
                targetingIsVirused: value.targetingIsVirused && {
                    joinType: mapJoinTypeToApiFormat(value.targetingIsVirused.joinType),
                    targetingMode: mapTargetingModeToApiFormat(value.targetingIsVirused.targetingMode)
                },
                startTime: value.startTime,
                finishTime: value.finishTime
            };
        default:
            throw new Error('unsupported group type');
    }
};

type MappedCpmData = [GdUpdateCpmAdGroupInput, Array<GroupId>];

export const groupGroupsByTypes = (groupsList: Record<GroupId, EditGroupData>): GroupsTypeMap => {
    return Object.keys(groupsList).reduce(
        (grouped: GroupsTypeMap, groupId: GroupId) => {
            const group = groupsList[groupId];

            switch (group.value.campaignType) {
                case CampaignType.TEXT:
                    grouped.text.push(group);
                    return grouped;
                case CampaignType.PERFORMANCE:
                    grouped.smart.push(group);
                    return grouped;
                case CampaignType.MCBANNER:
                    grouped.mcBanner.push(group);
                    return grouped;
                case CampaignType.CONTENT_PROMOTION:
                    grouped.contentPromotion.push(group);
                    return grouped;
                default:
                    return grouped;
            }
        },
        {
            text: [],
            smart: [],
            mcBanner: [],
            contentPromotion: []
        }
    );
};

export const splitGroupsByState = (groupsList: Array<EditGroupData>, features: ClientFeatures): GroupsStateMap => {
    return groupsList.reduce(
        (acc: GroupsStateMap, group) => {
            // todo удалить после открытия фичи на 100% DIRECT-109443
            if (group.value.campaignType === CampaignType.TEXT && !features.isHonestAddTextAdGroupAllowed) {
                acc.updated.push(group);
                return acc;
            }

            if (group.isNew) {
                acc.added.push(group);
            } else {
                acc.updated.push(group);
            }

            return acc;
        },
        { added: [], updated: [] }
    );
};

export const mapGroupsToSaveGroupsInput = (
    groupsData: Record<GroupId, EditGroupData>,
    cryptaSegmentsByType: CryptaSegmentsByType,
    campaignId: string,
    features: ClientFeatures
): SaveGroupsInput => {
    const groupedByType = groupGroupsByTypes(groupsData);
    const {
        text: { added: addedTextGroups, updated: updatedTextGroups },
        smart: { updated: updatedSmartGroups },
        mcBanner: { updated: updatedMcBannerGroups },
        contentPromotion: { added: addContentPromotionGroups, updated: updatedContentPromotionGroups }
    } = mapValues<GroupsTypeMap, GroupsStateMap>(groupedByType, groupsList => splitGroupsByState(groupsList, features));

    const addedTextGroupsInput = addedTextGroups.map(item => {
        const { adGroupName, adGroupId, ...group } = mapGroupToTextInput(item, cryptaSegmentsByType, true);

        return {
            ...group,
            name: adGroupName,
            campaignId
        };
    });
    const updatedTextGroupsInput = updatedTextGroups.map(item =>
        mapGroupToTextInput(item, cryptaSegmentsByType, false)
    );
    const updatedSmartGroupsInput = updatedSmartGroups.map(mapGroupToSmartInput);
    const updatedMcBannerGroupsInput = updatedMcBannerGroups.map(mapGroupToMcBannerInput);
    const updatedContentPromotionGroupsInput = updatedContentPromotionGroups.map(mapUpdateGroupToContentPromotionInput);
    const addedContentPromotionGroupsInput = addContentPromotionGroups.map(mapAddGroupToContentPromotionInput);

    return {
        textAddInput: addedTextGroupsInput,
        textUpdateInput: updatedTextGroupsInput,
        smartUpdateInput: updatedSmartGroupsInput,
        mcBannerUpdateInput: updatedMcBannerGroupsInput,
        contentPromotionAddInput: addedContentPromotionGroupsInput,
        contentPromotionUpdateInput: updatedContentPromotionGroupsInput
    };
};

export const getCategoryGroupIdsMap = (
    groupsData: Record<GroupId, EditGroupData>,
    features: ClientFeatures
): Record<GroupsCategories, string[]> => {
    const groupedByType = groupGroupsByTypes(groupsData);
    const {
        text: { added: addedTextGroups, updated: updatedTextGroups },
        smart: { updated: updatedSmartGroups },
        mcBanner: { updated: updatedMcBannerGroups },
        contentPromotion: { added: addedContentPromotionGroups, updated: updatedContentPromotionGroups }
    } = mapValues<GroupsTypeMap, GroupsStateMap>(groupedByType, groupsList => splitGroupsByState(groupsList, features));

    return {
        [GroupsCategories.textAdd]: addedTextGroups.map(g => g.id),
        [GroupsCategories.textUpdate]: updatedTextGroups.map(g => g.id),
        [GroupsCategories.smartUpdate]: updatedSmartGroups.map(g => g.id),
        [GroupsCategories.mcBannerUpdate]: updatedMcBannerGroups.map(g => g.id),
        [GroupsCategories.contentPromotionAdd]: addedContentPromotionGroups.map(g => g.id),
        [GroupsCategories.contentPromotionUpdate]: updatedContentPromotionGroups.map(g => g.id)
    };
};

export const mapCpmGroupsDataToApiFormat = (
    groupsList: Record<GroupId, EditGroupData>,
    productsList: ProductsList,
    cryptaSegmentsByType: CryptaSegmentsByType
): MappedCpmData => {
    const allGroups: Array<EditGroupData> = Object.keys(groupsList)
        .map((groupId: GroupId) => groupsList[groupId])
        .filter((group: EditGroupData) => group.isEditable === true);

    const cpmGroups: Array<EditGroupData> = allGroups.filter(
        ({ value }: EditGroupData) =>
            value.campaignType === CampaignType.CPM_BANNER || value.campaignType === CampaignType.CPM_PRICE
    );

    return [
        {
            isNewGroups: cpmGroups.some(({ isNew }) => isNew),
            updateCpmAdGroupItems: cpmGroups.map(cpmGroup =>
                mapGroupToCpmInput(cpmGroup, productsList, cryptaSegmentsByType)
            )
        },
        cpmGroups.map(({ id }) => id)
    ];
};

export const mapEditorRegionsToRegionsData = (
    groupId: GroupId,
    regions: EditGroupRegions,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    regionTree: Region[]
): EditGroupRegions => {
    const currentRegionsEditor = regionsEditor[groupId] || defaultRegionsEditorData;

    return {
        regionIds: currentRegionsEditor.isOpen
            ? RegionsHelpers.convertRegionsSelectedInfoToFlattenList(
                  RegionsHelpers.getSelectedRegionsInfo(regionTree, currentRegionsEditor.selectedMap)
              )
            : regions.regionIds,
        selectedMap: currentRegionsEditor.isOpen ? currentRegionsEditor.selectedMap : regions.selectedMap
    };
};

const getGroupMinusKeywords = (group: EditGroupData, minusPhraseEditor: MinusPhraseLibraryEditorState): string[] => {
    switch (group.value.campaignType) {
        case CampaignType.MCBANNER:
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
            if (minusPhraseEditor.data === undefined) {
                return group.value.minusKeywords;
            }

            let minusPhrasesFromMassActions: string[] = [];
            // Если были применены настройки из массовых действий, берем значения из редактора
            if (minusPhraseEditor.data[MassActionId.groupsEdit] !== undefined) {
                minusPhrasesFromMassActions = minusPhraseEditor.data[MassActionId.groupsEdit].minusKeywordsEditor.value;
            }

            // Если что-то менялось в редакторе, берем значения из открытого редактора
            // Или сохраненные значения. При этом изменения из массовых действий учитываются
            const minusPhrasesFromEditor =
                minusPhraseEditor.data[group.id] && minusPhraseEditor.data[group.id].minusKeywordsEditor.isOpen
                    ? minusPhraseEditor.data[group.id].minusKeywordsEditor.value
                    : group.value.minusKeywords;

            return minusPhrasesFromEditor && minusPhrasesFromEditor.length !== 0
                ? minusPhrasesFromEditor
                : minusPhrasesFromMassActions;
        default:
            return [];
    }
};

const getGroupAudienceTargetings = (
    groupId: GroupId,
    audienceTargetings: AudienceTargetingsValue,
    audienceTargetingsEditors: AudienceTargetingsEditorsState,
    interestsEditors: InterestsEditorsState,
    socialDemoEditors: CryptaSocialDemoEditorsState,
    groupFamilyEditors: CryptaGroupFamilyEditorsState,
    audioGenresEditors: CryptaAudioGenresEditorsState,
    behavioralEditors: CryptaBehavioralEditorsState,
    retargetingAudienceEditors: RetargetingAudienceEditorState
): AudienceTargetingsValue => {
    const audienceTargetingsValue = {};
    const interestsEditor = interestsEditors[groupId];
    const socialDemoEditor = socialDemoEditors[groupId];
    const groupFamilyEditor = groupFamilyEditors[groupId];
    const audioGenresEditor = audioGenresEditors[groupId] || cryptaAudioGenresEditorsInitialState;
    const behavioralEditor = behavioralEditors[groupId];
    const retargetingAudienceEditor = retargetingAudienceEditors[groupId];

    const socialDemo = socialDemoEditor
        ? socialDemoEditor.checkedItems
        : audienceTargetings[AudienceTargetingsType.socialDemo];
    const family = groupFamilyEditor
        ? groupFamilyEditor.checkedItems
        : audienceTargetings[AudienceTargetingsType.family];
    const audioGenres = audioGenresEditor.isOpen
        ? audioGenresEditor.checkedItems
        : audienceTargetings[AudienceTargetingsType.audioGenres];
    const behaviors = behavioralEditor
        ? behavioralEditor.checkedItems
        : audienceTargetings[AudienceTargetingsType.behaviors];
    const interests = interestsEditor ? interestsEditor.value : audienceTargetings[AudienceTargetingsType.interests];
    let retargeting;

    if (retargetingAudienceEditor) {
        if (!retargetingAudienceEditor.isDeleting) {
            retargeting = retargetingAudienceEditor;
        }
    } else {
        retargeting = audienceTargetings[AudienceTargetingsType.audienceMetrics];
    }

    if (socialDemo) {
        audienceTargetingsValue[AudienceTargetingsType.socialDemo] = socialDemo;
    }

    if (family) {
        audienceTargetingsValue[AudienceTargetingsType.family] = family;
    }

    if (audioGenres) {
        audienceTargetingsValue[AudienceTargetingsType.audioGenres] = audioGenres;
    }

    if (behaviors) {
        audienceTargetingsValue[AudienceTargetingsType.behaviors] = behaviors;
    }

    if (interests) {
        audienceTargetingsValue[AudienceTargetingsType.interests] = interests;
    }

    if (retargeting) {
        audienceTargetingsValue[AudienceTargetingsType.audienceMetrics] = retargeting;
    }

    return audienceTargetingsValue;
};

const getGroupSelectedLibraryList = (
    group: EditGroupData,
    minusPhraseEditor: MinusPhraseLibraryEditorState
): MinusPhrasesShortLibraryItem[] | undefined => {
    switch (group.value.campaignType) {
        case CampaignType.TEXT:
        case CampaignType.PERFORMANCE:
        case CampaignType.MCBANNER:
            if (minusPhraseEditor.data === undefined) {
                return group.value.selectedLibraryList;
            }

            let selectedLibraryListFromMassActions: MinusPhrasesShortLibraryItem[] = [];
            // Если были применены настройки из массовых действий, берем значения из редактора
            if (minusPhraseEditor.data[MassActionId.groupsEdit] !== undefined) {
                selectedLibraryListFromMassActions = (
                    minusPhraseEditor.data[MassActionId.groupsEdit].minusKeywordsEditor.selectedLibraryListItems || []
                ).map(list => {
                    return { id: list, name: '' };
                });
            }

            // Если что-то менялось в редакторе, берем значения из открытого редактора
            // Или сохраненные значения. При этом изменения из массовых действий учитываются
            const selectedLibraryListFromEditor =
                minusPhraseEditor.data[group.id] && minusPhraseEditor.data[group.id].minusKeywordsEditor.isOpen
                    ? (minusPhraseEditor.data[group.id].minusKeywordsEditor.selectedLibraryListItems || []).map(
                          list => {
                              return { id: list, name: '' };
                          }
                      )
                    : group.value.selectedLibraryList;

            return selectedLibraryListFromEditor && selectedLibraryListFromEditor.length !== 0
                ? selectedLibraryListFromEditor
                : selectedLibraryListFromMassActions;
        default:
            return [];
    }
};

const getGroupRateCorrections = (
    editorData: Record<GroupId, RateCorrectionEditorState>,
    groupId: GroupId,
    rateCorrections: Record<RateCorrectionType, RateCorrection>
): RatesCorrectionsValue =>
    editorData[groupId] && editorData[groupId].isVisible
        ? mapEditorRateCorrectionsToRateCorrections(editorData[groupId].value)
        : rateCorrections;

export const getGroupsValueFromOpenedEditors = (
    groupsData: Record<GroupId, EditGroupData>,
    rateCorrectionsData: Record<GroupId, RateCorrectionEditorState>,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    retargetingEditor: Record<GroupId, RetargetingEditorState>,
    keywordsEditor: Record<string, KeywordsEditorData>,
    minusPhraseEditor: MinusPhraseLibraryEditorState,
    nameEditors: EditPageItemNameEditorsState,
    audienceTargetingsEditor: AudienceTargetingsEditorsState,
    interestsEditor: InterestsEditorsState,
    socialDemoEditor: CryptaSocialDemoEditorsState,
    groupFamilyEditor: CryptaGroupFamilyEditorsState,
    audioGenresEditor: CryptaAudioGenresEditorsState,
    behavioralEditor: CryptaBehavioralEditorsState,
    retargetingAudienceEditor: Record<GroupId, RetargetingConditionAudienceEditorState>,
    piTagsEditor: PITagsEditorsState,
    targetTagsEditor: TargetTagsEditorsState,
    regionTree: Region[],
    editableTypes: Set<GroupType>,
    features: ClientFeatures
): Record<GroupId, EditGroupData> => {
    return Object.keys(groupsData).reduce((acc: Record<GroupId, EditGroupData>, key: GroupId) => {
        const group = groupsData[key];

        if (editableTypes.has(group.value.groupType)) {
            acc[key] = getValueForEditGroup(
                group,
                rateCorrectionsData,
                regionsEditor,
                retargetingEditor,
                keywordsEditor,
                minusPhraseEditor,
                nameEditors,
                audienceTargetingsEditor,
                interestsEditor,
                socialDemoEditor,
                groupFamilyEditor,
                audioGenresEditor,
                behavioralEditor,
                regionTree,
                retargetingAudienceEditor,
                piTagsEditor,
                targetTagsEditor,
                features
            );
        }

        return acc;
    }, {});
};

function mergePiTagsEditors(
    group: EditGroupData,
    features: ClientFeatures,
    targetTagsEditor: TargetTagsEditorsState,
    piTagsEditor?: PITagsEditorState
): PITagsEditorState | undefined {
    const { isZeroSpeedPageForGeoproductEnabled } = features;
    let piTagsData = piTagsEditor;
    const editorId = GroupsHelpers.getGroupEditPageItemEditorId(group.id);

    if (group.value.groupType === GroupType.CPM_GEOPRODUCT) {
        const targetTagsData = targetTagsEditor[editorId] && targetTagsEditor[editorId].platforms;

        let targetTags = targetTagsData ? Object.keys(targetTagsData) : [];
        let selectedTargetTags = targetTagsData ? targetTags.filter(tag => targetTagsData[tag].selected) : [];

        if (!isZeroSpeedPageForGeoproductEnabled) {
            targetTags = [TargetPlatformTags.APP_METRO];
            selectedTargetTags = [TargetPlatformTags.APP_METRO];
        }

        // Убрать из общего списка тэгов те, которые используются для выбора площадки
        const relevantPiTargetTags =
            (piTagsData &&
                piTagsData.targetTags &&
                piTagsData.targetTags.split(',').filter(tag => {
                    return !targetTags.includes(tag.trim());
                })) ||
            [];

        // Убрать из общего списка тэгов те, которые используются для выбора площадки
        const relevantPiPageGroupTags =
            (piTagsData &&
                piTagsData.pageGroupTags &&
                piTagsData.pageGroupTags.split(',').filter(tag => {
                    return !targetTags.includes(tag.trim());
                })) ||
            [];

        piTagsData = {
            // Добавать к отфильтрованному общему списку только выбранные площадки
            targetTags: uniq([...relevantPiTargetTags, ...selectedTargetTags]).join(','),
            pageGroupTags: uniq([...relevantPiPageGroupTags, ...selectedTargetTags]).join(',')
        };
    }

    return piTagsData;
}

function getValueForEditGroup(
    group: EditGroupData,
    rateCorrectionsData: Record<GroupId, RateCorrectionEditorState>,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    retargetingEditor: Record<GroupId, RetargetingEditorState>,
    keywordsEditor: Record<string, KeywordsEditorData>,
    minusPhraseEditor: MinusPhraseLibraryEditorState,
    nameEditors: EditPageItemNameEditorsState,
    audienceTargetingsEditor: AudienceTargetingsEditorsState,
    interestsEditor: InterestsEditorsState,
    socialDemoEditor: CryptaSocialDemoEditorsState,
    groupFamilyEditor: CryptaGroupFamilyEditorsState,
    audioGenresEditor: CryptaAudioGenresEditorsState,
    behavioralEditor: CryptaBehavioralEditorsState,
    regionTree: Region[],
    retargetingAudienceEditor: Record<GroupId, RetargetingConditionAudienceEditorState>,
    piTagsEditor: PITagsEditorsState,
    targetTagsEditor: TargetTagsEditorsState,
    features: ClientFeatures
): EditGroupData {
    const groupNameEditor = selectEditPageItemNameEditorValue(
        nameEditors,
        GroupsHelpers.getGroupEditPageItemEditorId(group.id)
    );

    const keywordsEditorData = keywordsEditor[getGroupKeywordsEditorId(group.id)];

    switch (group.value.campaignType) {
        case CampaignType.TEXT:
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible ? groupNameEditor.value : group.value.name,
                    keywords:
                        keywordsEditorData && keywordsEditorData.isOpen
                            ? keywordsEditorData.value
                            : group.value.keywords,
                    regions: mapEditorRegionsToRegionsData(group.id, group.value.regions, regionsEditor, regionTree),
                    retargeting:
                        retargetingEditor[group.id] && retargetingEditor[group.id].isVisible
                            ? mapRetargetingEditorValueToGroupRetargetingValue(
                                  retargetingEditor[group.id].conditions,
                                  group.value.retargeting
                              )
                            : group.value.retargeting,
                    audienceTargetingsTemplateName: audienceTargetingsEditor[group.id]
                        ? audienceTargetingsEditor[group.id].templateName
                        : group.value.audienceTargetingsTemplateName,
                    audienceTargetings: getGroupAudienceTargetings(
                        group.id,
                        group.value.audienceTargetings,
                        audienceTargetingsEditor,
                        interestsEditor,
                        socialDemoEditor,
                        groupFamilyEditor,
                        audioGenresEditor,
                        behavioralEditor,
                        retargetingAudienceEditor
                    ),
                    rateCorrections: getGroupRateCorrections(
                        rateCorrectionsData,
                        group.id,
                        group.value.rateCorrections
                    ),
                    minusKeywords: getGroupMinusKeywords(group, minusPhraseEditor),
                    selectedLibraryList: getGroupSelectedLibraryList(group, minusPhraseEditor),
                    piTagsEditor: piTagsEditor[group.id] ? piTagsEditor[group.id] : group.value.piTagsEditor
                }
            };

        case CampaignType.MCBANNER:
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible ? groupNameEditor.value : group.value.name,
                    keywords:
                        keywordsEditorData && keywordsEditorData.isOpen
                            ? keywordsEditorData.value
                            : group.value.keywords,
                    regions: mapEditorRegionsToRegionsData(group.id, group.value.regions, regionsEditor, regionTree),
                    rateCorrections: getGroupRateCorrections(
                        rateCorrectionsData,
                        group.id,
                        group.value.rateCorrections
                    ),
                    minusKeywords: getGroupMinusKeywords(group, minusPhraseEditor),
                    selectedLibraryList: getGroupSelectedLibraryList(group, minusPhraseEditor),
                    piTagsEditor: piTagsEditor[group.id] ? piTagsEditor[group.id] : group.value.piTagsEditor
                }
            };

        case CampaignType.PERFORMANCE:
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible ? groupNameEditor.value : group.value.name,
                    regions: mapEditorRegionsToRegionsData(group.id, group.value.regions, regionsEditor, regionTree),
                    minusKeywords: getGroupMinusKeywords(group, minusPhraseEditor),
                    selectedLibraryList: getGroupSelectedLibraryList(group, minusPhraseEditor),
                    rateCorrections: getGroupRateCorrections(
                        rateCorrectionsData,
                        group.id,
                        group.value.rateCorrections
                    ),
                    piTagsEditor: piTagsEditor[group.id] ? piTagsEditor[group.id] : group.value.piTagsEditor
                }
            };

        case CampaignType.CPM_BANNER: {
            const piTags = piTagsEditor[group.id] ? piTagsEditor[group.id] : group.value.piTagsEditor;

            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible ? groupNameEditor.value : group.value.name,
                    keywords:
                        keywordsEditorData && keywordsEditorData.isOpen
                            ? keywordsEditorData.value
                            : group.value.keywords,
                    regions: mapEditorRegionsToRegionsData(group.id, group.value.regions, regionsEditor, regionTree),
                    audienceTargetings: getGroupAudienceTargetings(
                        group.id,
                        group.value.audienceTargetings,
                        audienceTargetingsEditor,
                        interestsEditor,
                        socialDemoEditor,
                        groupFamilyEditor,
                        audioGenresEditor,
                        behavioralEditor,
                        retargetingAudienceEditor
                    ),
                    audienceTargetingsTemplateName: audienceTargetingsEditor[group.id]
                        ? audienceTargetingsEditor[group.id].templateName
                        : group.value.audienceTargetingsTemplateName,
                    rateCorrections:
                        rateCorrectionsData[group.id] && rateCorrectionsData[group.id].isVisible
                            ? mapEditorRateCorrectionsToRateCorrections(rateCorrectionsData[group.id].value)
                            : group.value.rateCorrections,
                    piTagsEditor: mergePiTagsEditors(group, features, targetTagsEditor, piTags)
                }
            };
        }

        case CampaignType.CPM_PRICE: {
            return {
                ...group,
                value: {
                    ...group.value,
                    name: groupNameEditor.isVisible ? groupNameEditor.value : group.value.name,
                    piTagsEditor: piTagsEditor[group.id] ? piTagsEditor[group.id] : group.value.piTagsEditor,
                    regions: mapEditorRegionsToRegionsData(group.id, group.value.regions, regionsEditor, regionTree),
                    audienceTargetingsTemplateName: audienceTargetingsEditor[group.id]
                        ? audienceTargetingsEditor[group.id].templateName
                        : group.value.audienceTargetingsTemplateName,
                    audienceTargetings: getGroupAudienceTargetings(
                        group.id,
                        group.value.audienceTargetings,
                        audienceTargetingsEditor,
                        interestsEditor,
                        socialDemoEditor,
                        groupFamilyEditor,
                        audioGenresEditor,
                        behavioralEditor,
                        retargetingAudienceEditor
                    )
                }
            };
        }

        default:
            return group;
    }
}

const getDateStringFromDateEditor = (date?: moment.Moment, time?: string): GraphQlLocalDateTime | undefined => {
    if (!date) {
        return undefined;
    }

    const dateString = date.format('YYYY-MM-DD');

    if (!time) {
        return `${dateString}T00:00`;
    }

    return `${dateString}T${time}`;
};

export const getInternalGroupsValueFromEditors = (
    groupsData: Record<GroupId, EditGroupData>,
    regionsEditor: Record<GroupId, RegionsEditorState>,
    nameEditors: EditPageItemNameEditorsState,
    dateEditors: DatesIntervalEditorsState,
    targetingEditors: TagCloudTargetingsState,
    regionTree: Region[],
    stringDicts: StringDicts = {}
): Record<GroupId, EditInternalGroupData> => {
    return mapValues(groupsData, (group: EditInternalGroupData) => {
        const groupNameEditor = selectEditPageItemNameEditorValue(
            nameEditors,
            GroupsHelpers.getGroupEditPageItemEditorId(group.id)
        );
        const dateEditor = dateEditors[group.id];
        const targetingShowDates =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.SHOW_DATES, group.id)];
        const targetingYandexUids =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.YANDEX_UIDS, group.id)];
        const targetingDeviceNames =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.DEVICE_NAMES, group.id)];
        const targetingUserAgents =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.USER_AGENTS, group.id)];
        const targetingQueryReferers =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.QUERY_REFERERS, group.id)];
        const targetingClidTypes =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.CLID_TYPES, group.id)];
        const targetingClids = targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.CLIDS, group.id)];
        const targetingMobileInstalledApps =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.MOBILE_INSTALLED_APPS, group.id)];
        const targetingQueryOptions =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.QUERY_OPTIONS, group.id)];
        const targetingTestIds = targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.TEST_IDS, group.id)];
        const targetingYsCookies =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.YS_COOKIES, group.id)];
        const targetingBrowserNames =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.BROWSER_NAMES, group.id)];
        const targetingBrowserEngines =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.BROWSER_ENGINES, group.id)];
        const targetingDeviceVendors =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.DEVICE_VENDORS, group.id)];
        const targetingOsNames = targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.OS_NAMES, group.id)];
        const targetingOsFamilies =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.OS_FAMILIES, group.id)];
        const targetingDesktopInstalledApps =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.DESKTOP_INSTALLED_APPS, group.id)];
        const targetingInterfaceLangs =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.INTERFACE_LANGS, group.id)];
        const targetingIsYandexPlus =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.IS_YANDEX_PLUS, group.id)];
        const targetingIsVirused =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.IS_VIRUSED, group.id)];
        const targetingInternalNetwork =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.INTERNAL_NETWORK, group.id)];

        const targetingDeviceOptions =
            targetingEditors[getTagCloudTargetingId(AdditionalTargetingField.DEVICE_OPTIONS, group.id)];

        switch (group.value.campaignType) {
            case CampaignType.INTERNAL_DISTRIB:
            case CampaignType.INTERNAL_FREE:
            case CampaignType.INTERNAL_AUTOBUDGET:
                return {
                    ...group,
                    value: {
                        ...group.value,
                        name: groupNameEditor.isVisible ? groupNameEditor.value : group.value.name,
                        regions: mapEditorRegionsToRegionsData(
                            group.id,
                            group.value.regions,
                            regionsEditor,
                            regionTree
                        ),
                        startTime: getDateStringFromDateEditor(dateEditor.startDate, dateEditor.startTime),
                        finishTime: getDateStringFromDateEditor(dateEditor.endDate, dateEditor.endTime),
                        targetingShowDates: mapTagCloudTargetingTextValue(targetingShowDates),
                        targetingYandexUids: mapTagCloudTargetingTextValue(targetingYandexUids),
                        targetingDeviceNames: mapTagCloudTargetingTextValue(targetingDeviceNames),
                        targetingUserAgents: mapTagCloudTargetingTextValue(targetingUserAgents),
                        targetingQueryReferers: mapTagCloudTargetingTextValue(targetingQueryReferers),
                        targetingClidTypes: mapTagCloudTargetingTextValue(targetingClidTypes),
                        targetingClids: mapTagCloudTargetingTextValue(targetingClids),
                        targetingMobileInstalledApps: mapTagCloudTargetingTextValue(targetingMobileInstalledApps),
                        targetingQueryOptions: mapTagCloudTargetingTextValue(targetingQueryOptions),
                        targetingTestIds: mapTagCloudTargetingTextValue(targetingTestIds),
                        targetingYsCookies: mapTagCloudTargetingTextValue(targetingYsCookies),
                        targetingBrowserNames: mapTagCloudTargetingSuggestWithVersionValue(
                            targetingBrowserNames,
                            stringDicts.UA_TRAITS_BROWSER_NAME
                        ),
                        targetingBrowserEngines: mapTagCloudTargetingSuggestWithVersionValue(
                            targetingBrowserEngines,
                            stringDicts.UA_TRAITS_BROWSER_ENGINE
                        ),
                        targetingOsFamilies: mapTagCloudTargetingSuggestWithVersionValue(
                            targetingOsFamilies,
                            stringDicts.UA_TRAITS_OS_FAMILY
                        ),
                        targetingDeviceVendors: mapTagCloudTargetingSuggestStringDictValue(
                            targetingDeviceVendors,
                            stringDicts.UA_TRAITS_DEVICE_VENDOR
                        ),
                        targetingOsNames: mapTagCloudTargetingSuggestStringDictValue(
                            targetingOsNames,
                            stringDicts.UA_TRAITS_OS_NAME
                        ),
                        targetingDesktopInstalledApps: mapTagCloudTargetingSuggestValue(
                            targetingDesktopInstalledApps,
                            stringDicts.DISTRIB_SOFT
                        ),
                        targetingInterfaceLangs: mapTagCloudTargetingSuggestValue(
                            targetingInterfaceLangs,
                            fetchInterfaceLangs()
                        ),
                        targetingIsYandexPlus: mapTagCloudRadioTargetingEditorData(targetingIsYandexPlus),
                        targetingIsVirused: mapTagCloudRadioTargetingEditorData(targetingIsVirused),
                        targetingInternalNetwork: mapTagCloudRadioTargetingEditorData(targetingInternalNetwork),
                        ...mapTagCloudTargetingToDeviceOptionsRecord(targetingDeviceOptions)
                    }
                };
            default:
                return group;
        }
    });
};

export const mapDuplicateGroupsApiReponseData = (data: CopyAdGroupsInfoQuery): Array<DuplicateItem> => {
    const {
        client: {
            adGroups: { rowset }
        }
    } = data;

    return rowset.map(item => {
        let isDisabled;

        switch (item.campaign.type) {
            case GdCampaignType.TEXT:
            case GdCampaignType.CPM_BANNER:
            case GdCampaignType.CONTENT_PROMOTION:
                isDisabled = false;
                break;
            default:
                isDisabled = true;
        }

        return {
            campaignName: item.campaign.name,
            groupName: item.name,
            groupId: item.id,
            campaignId: item.campaign.id,
            campaignType: mapCampaignRowTypeToCampaignType(item.campaign.type),
            isInvalid: false,
            isDisabled
        };
    });
};

// этот метод нужен для предсказуемого упорядочивания кампаний и групп на странице редактирования
export const groupGroupsByCampaignId = (
    groupsHash: Record<GroupId, EditGroupData>
): Record<string, Array<EditGroupData>> => {
    return reduce(
        map(
            groupBy(groupsHash, group => group.campaign.id),
            (groupsArr: Array<EditGroupData>, campaignId: string): Record<string, Array<EditGroupData>> => ({
                [campaignId]: sortBy(groupsArr, ({ id }: EditGroupData): number => Number(id))
            })
        ),
        (acc, groupItems) => ({ ...acc, ...groupItems }),
        {}
    );
};

const mapInterfaceLangToApiFormat = (interfaceLangs: string[]): InterfaceLangInput[] => {
    return interfaceLangs.reduce((acc, interfaceLang): InterfaceLangInput[] => {
        if (!Object.values(InterfaceLangInput).includes(interfaceLang)) {
            throw new Error(`Для значения ${interfaceLang} не определено api значение`);
        }

        return [...acc, InterfaceLangInput[interfaceLang]];
    }, []);
};

const mapFeedFilterConditionToApiFormat = (
    field: string,
    operator: GdSmartFilterConditionOperator,
    value: FeedFilterOperationControlValues | boolean | number
): GdAddSmartFilterConditionInput => {
    return {
        field,
        operator: GdSmartFilterConditionOperatorInput[operator],
        stringValue: JSON.stringify(value)
    };
};

const mapFeedFilterTreeTabConditionsToApiFormat = (
    filter: EditGroupFilterItem
): Array<GdAddSmartFilterConditionInput> => {
    const { priceRange, vendors, feedCategories } = filter;
    const conditions = [];

    const [minPrice, maxPrice] = priceRange;
    if (minPrice !== 0 || maxPrice !== 0) {
        const priceRangeCondition = mapFeedFilterConditionToApiFormat('price', GdSmartFilterConditionOperator.RANGE, [
            priceRange.join('-')
        ]);

        conditions.push(priceRangeCondition);
    }

    if (vendors) {
        const vendorsCondition = mapFeedFilterConditionToApiFormat(
            'vendor',
            GdSmartFilterConditionOperator.CONTAINS_ANY,
            vendors.split(',')
        );

        conditions.push(vendorsCondition);
    }

    const selectedCategories = VirtualTreeHelpers.filterNodes(feedCategories, VirtualTreeHelpers.isSelected);
    const flatCategories = VirtualTreeHelpers.getFlattenedTree(selectedCategories).map(category => String(category.id));

    const categoriesIdsCondition = mapFeedFilterConditionToApiFormat(
        'categoryId',
        GdSmartFilterConditionOperator.EQUALS_ANY,
        flatCategories
    );

    return conditions.concat(categoriesIdsCondition);
};

const mapFeedFilterConditionTabConditionsToApiFormat = (
    filter: EditGroupFilterItem
): Array<GdAddSmartFilterConditionInput> => {
    return filter.conditions.map(condition => {
        const { operator, field, arguments: values } = condition;

        if (operator === GdSmartFilterConditionOperator.EXISTS) {
            return mapFeedFilterConditionToApiFormat(field, operator, 1);
        }

        if (operator === GdSmartFilterConditionOperator.RANGE) {
            return mapFeedFilterConditionToApiFormat(
                field,
                operator,
                (values as Array<NumberRangeInputValues>).map(range => range.join('-'))
            );
        }

        return mapFeedFilterConditionToApiFormat(field, operator, values);
    });
};

const mapFeedFilterConditionsToApiFormat = (
    filter: EditGroupFilterItem,
    schema: FeedFilterSchema
): Array<GdAddSmartFilterConditionInput> => {
    const { tab, isAvailable } = filter;

    const isAvailableCondition = mapFeedFilterConditionToApiFormat(
        'available',
        GdSmartFilterConditionOperator.EQUALS_ANY,
        isAvailable
    );

    const hasAvailableInSchema = schemaHasAvailableCondition(schema);

    const conditions = hasAvailableInSchema ? [isAvailableCondition] : [];

    switch (tab) {
        case GdSmartFilterTab.ALL_PRODUCTS:
            return [];
        case GdSmartFilterTab.TREE:
            return [...mapFeedFilterTreeTabConditionsToApiFormat(filter), ...conditions];
        case GdSmartFilterTab.CONDITION:
            return [...mapFeedFilterConditionTabConditionsToApiFormat(filter), ...conditions];
        default:
            throw new Error('Таб не определен');
    }
};

export const mapFeedFilterDataToAddApiFormat = (
    filter: EditGroupFilterItem,
    groupId: GroupId,
    schema: FeedFilterSchema,
    campaignStrategy?: GdStrategyType
): GdAddSmartFiltersInput => {
    const budgetCalculation = defineSmartFilterBudgetCalculation(campaignStrategy);

    return {
        addItems: [
            {
                ...mapFeedFilterPricesToApiFormat(filter, budgetCalculation),
                tab: GdSmartFilterTabInput[filter.tab],
                adGroupId: groupId,
                name: filter.name,
                targetFunnel: GdSmartFilterTargetFunnelInput[filter.type],
                isSuspended: false,
                conditions: mapFeedFilterConditionsToApiFormat(filter, schema)
            }
        ]
    };
};

export const mapFeedFilterDataToUpdateApiFormat = (
    filter: EditGroupFilterItem,
    schema: FeedFilterSchema,
    campaignStrategy?: GdStrategyType
): GdUpdateSmartFiltersInput => {
    const budgetCalculation = defineSmartFilterBudgetCalculation(campaignStrategy);

    return {
        updateItems: [
            {
                ...mapFeedFilterPricesToApiFormat(filter, budgetCalculation),
                tab: GdSmartFilterTabInput[filter.tab],
                id: filter.id,
                name: filter.name,
                targetFunnel: GdSmartFilterTargetFunnelInput[filter.type],
                isSuspended: false,
                conditions: mapFeedFilterConditionsToApiFormat(filter, schema)
            }
        ]
    };
};

export const mapBidValueToApiFormat = (bidValue?: number): number => {
    const resetBidValue = 0;

    return isUndefined(bidValue) ? resetBidValue : bidValue;
};

const mapFeedFilterPricesToApiFormat = (
    filter: EditGroupFilterItem,
    budgetCalculation: SmartGroupBudgetCalculation
): {
    priceCpc?: string;
    priceCpa?: string;
    autobudgetPriority?: GdShowConditionAutobudgetPriorityInput;
} => {
    const { priceCpa, priceCpc, autobudgetPriority } = filter;

    const formattedPriceCpa = String(mapBidValueToApiFormat(priceCpa));
    const formattedPriceCpc = String(mapBidValueToApiFormat(priceCpc));

    switch (budgetCalculation) {
        case SmartGroupBudgetCalculation.FULL:
            return { priceCpc: formattedPriceCpc, priceCpa: formattedPriceCpa };
        case SmartGroupBudgetCalculation.CPC:
            return { priceCpc: formattedPriceCpc };
        case SmartGroupBudgetCalculation.AUTO:
        default:
            return {
                autobudgetPriority: autobudgetPriority && GdShowConditionAutobudgetPriorityInput[autobudgetPriority]
            };
    }
};

const mapFeedFilterEnumParamsFromApiResponse = (
    values: FeedFilterEnumFieldParams['values'],
    fieldName: string
): FeedFilterEnumFieldParams['values'] => {
    if (fieldName === 'age') {
        return values.sort((a, b) => Number(a) - Number(b));
    }

    return values;
};

const mapFeedFilterOperatorFromApiResponse = (
    filter: SmartFeedSchemaQuery_constants_smartFilterSchemas_fields_GdSmartFilterStringField_operators
): FeedFilterSchemaOperator => {
    return {
        maxItems: filter.maxItems,
        type: filter.type
    };
};

export const mapFeedFilterSchemaFromApiResponse = (data: SmartFeedSchemaQuery): FeedFilterSchema => {
    const [schema] = data.constants.smartFilterSchemas || [null];
    return {
        fields: !schema
            ? []
            : (schema.fields
                  .map((field: SmartFeedSchemaQuery_constants_smartFilterSchemas_fields):
                      | FeedFilterSchemaField
                      | undefined => {
                      switch (field.__typename) {
                          case 'GdSmartFilterNumberField':
                              return {
                                  fieldType: FeedFilterSchemaFieldType.NUMBER,
                                  name: field.name,
                                  max: field.max,
                                  min: field.min,
                                  precision: field.precision,
                                  operators: field.operators.map(mapFeedFilterOperatorFromApiResponse)
                              };
                          case 'GdSmartFilterStringField':
                              return {
                                  fieldType: FeedFilterSchemaFieldType.STRING,
                                  name: field.name,
                                  operators: field.operators.map(mapFeedFilterOperatorFromApiResponse),
                                  maxLength: field.maxLength,
                                  minLength: field.minLength,
                                  pattern: getValueFromApiFormat(field.pattern)
                              };

                          case 'GdSmartFilterEnumField':
                              return {
                                  fieldType: FeedFilterSchemaFieldType.ENUM,
                                  name: field.name,
                                  operators: field.operators.map(mapFeedFilterOperatorFromApiResponse),
                                  values: mapFeedFilterEnumParamsFromApiResponse(field.values, field.name)
                              };
                          default:
                              return;
                      }
                  })
                  .filter(Boolean) as Array<FeedFilterSchemaField>)
    };
};

export const defineSmartFilterBudgetCalculation = (
    campaignStrategyType?: GdStrategyType
): SmartGroupBudgetCalculation => {
    switch (campaignStrategyType) {
        case GdStrategyType.AVG_CPA_PER_CAMP:
        case GdStrategyType.AVG_CPA_PER_FILTER:
            return SmartGroupBudgetCalculation.FULL;
        case GdStrategyType.AVG_CPC_PER_CAMP:
        case GdStrategyType.AVG_CPC_PER_FILTER:
            return SmartGroupBudgetCalculation.CPC;
        default:
            return SmartGroupBudgetCalculation.AUTO;
    }
};

export const isSmartFilterTreeSchema = (schema: FeedFilterSchema): boolean => {
    return schema.fields.some(field => field.name === 'categoryId');
};

export const schemaHasAvailableCondition = (schema: FeedFilterSchema): boolean => {
    return schema.fields.some(field => field.name === 'available');
};

export const getFiltersSaveValidationResult = ({
    errors,
    data
}: FetchResult<AddSmartFilters | UpdateSmartFilters>): Array<ValidationResultItem> => {
    const filterMutationOperations = ['addSmartFilters', 'updateFilters'];

    return filterMutationOperations.reduce((acc, operation) => {
        const currentErrors = get(data, `${operation}.validationResult.errors`, []).map((error: ValidationError) => {
            return pick(error, ['code', 'params', 'path']);
        });
        return [...acc, ...currentErrors];
    }, []);
};

const getDefaultTraitsSelectItems = (): StringDictsItems => {
    return {
        [GdStringDictName.UA_TRAITS_BROWSER_ENGINE]: [],
        [GdStringDictName.UA_TRAITS_BROWSER_NAME]: [],
        [GdStringDictName.UA_TRAITS_DEVICE_VENDOR]: [],
        [GdStringDictName.UA_TRAITS_OS_FAMILY]: [],
        [GdStringDictName.UA_TRAITS_OS_NAME]: []
    };
};

export const mapUaTraitApiResponseToSelectItems = (response: ApolloQueryResult<UaTraitsQuery>): StringDictsItems => {
    const defaultTraitsSelectItems = getDefaultTraitsSelectItems();

    if (!response.data.constants.stringDicts) {
        return defaultTraitsSelectItems;
    }

    return response.data.constants.stringDicts.reduce((acc, stringDict) => {
        if (!stringDict) {
            return {
                ...defaultTraitsSelectItems,
                ...acc
            };
        }

        return {
            ...acc,
            [stringDict.dictName]: stringDict.entries.map(entry => ({
                val: (entry as UaTraitsQuery_constants_stringDicts_entries_GdStringDictEntryWithLongKey).key.toString(),
                text: entry.value
            }))
        };
    }, {});
};

export const mapGroupEditRecommendationsToQueryResponse = (
    response: ApolloQueryResult<RecommendationsGroupEditQuery>
): Record<string, EditPageRecommendationsData> => {
    const adGroupsRowset = response.data.client.adGroups.rowset;

    return adGroupsRowset.reduce(
        (
            result: Record<string, EditPageRecommendationsData>,
            row: RecommendationsAdsGroupEditQuery_client_adGroups_rowset
        ) => {
            const { recommendations, id, type } = row;

            if (recommendations && recommendations.length) {
                result[id] = {
                    recommendations: recommendations
                        .filter(recommendation => isRecommendationSupportedOnEditGroupPage(recommendation.type, type))
                        .map(mapBaseRecommendationInfoFromApiFormat),
                    isRecommendationsVisible: true
                };
            }

            return result;
        },
        {}
    );
};

export const mapGroupEditGeoTreeFromApi = (
    response: ApolloQueryResult<RegionTreeForGroupEdit>
): Record<string, Region[]> => {
    const adGroupsRowset = response.data.client.campaigns.rowset.filter(item => item.__typename === 'GdPriceCampaign');
    return adGroupsRowset.reduce(
        (acc: Record<string, Region[]>, item: RegionTreeForGroupEdit_client_campaigns_rowset_GdPriceCampaign) => {
            acc[item.id] = item.customGeoExpanded.geoExpandedTrees.map(mapGeoTreeFromApi);

            return acc;
        },
        {}
    );
};

export const mapGeoTreeFromApi = (
    response: RegionTreeForGroupEdit_client_campaigns_rowset_GdPriceCampaign_customGeoExpanded_geoExpandedTrees
): Region => {
    const { name, id, inner } = response;

    return {
        name: name,
        id: String(id),
        inner: inner ? inner.map(mapGeoTreeFromApi) : null
    };
};
